<script>
    import {
        SvelteFlow,
        Background,
        Controls,
        MiniMap,
        MarkerType,
        ConnectionMode,
        ViewportPortal,
        useSvelteFlow,
    } from "@xyflow/svelte";
    import { setContext, onMount, tick, untrack } from "svelte";
    import "@xyflow/svelte/dist/style.css";
    import dagre from "@dagrejs/dagre";
    import {
        clampPanelPosition,
        draggedPanelPosition,
        parsePanelState,
    } from "./control-panel.js";
    import { planLayout } from "./layout.js";
    import ResearchCard from "./ResearchCard.svelte";
    import ResearchEdge from "./ResearchEdge.svelte";
    import { separateEdgeLabels } from "./edge-labels.js";
    import {
        buildRunbookPhaseFrames,
        layoutRunbookBranch,
        makeRoomForBranch,
        layoutRunbookLine,
        preserveRunbookExpansionContext,
        runbookHierarchySpacing,
        separateRunbookPhaseBranches,
    } from "./runbook-frames.js";
    import { applyOps, diffGraph } from "./ops.js";
    import {
        initialsOf,
        peopleFrom,
        sweepStale,
        workingStyles,
    } from "./presence.js";
    import {
        RUNBOOK_EDGE_ROLES,
        defaultRunbookCollapse,
        nextRunbookBranchExpansion,
        nextRunbookExpansion,
        projectRunbookGraph,
        resolveRunbookConnection,
        restoreRunbookCollapse,
        runbookDependencyNeighborhood,
        runbookEdgeRole,
        runbookHierarchy,
        runbookStartNodeId,
    } from "./runbook-graph.js";
    import { sizeParents } from "./sizing.js";

    let {
        initial,
        initialVersion,
        initialSeq = 0,
        saveUrl,
        opsUrl,
        cardsUrl,
        pinUrl,
        attachUrl,
        csrf,
        canEdit,
        sandbox = false,
        // Which pieces of canvas chrome to draw. The app wants all of it; the public demo
        // hides the card-creation bar (its actions need endpoints a demo has none of) while
        // keeping zoom and the minimap, which are the parts a visitor actually uses.
        showCardTools = true,
        showControls = true,
        showMinimap = true,
        runbookFlow = false,
        runbookFingerprint = "",
        stateKey = "",
        boardId = "",
        contentTypes = [],
        me = null,
        members = [],
        onStatus,
    } = $props();

    // What cards need from the flow, kept OFF the node payload so node data stays cloneable.
    setContext("zar-research-card", {
        canEdit,
        rename: (entryId, title) => commitRename(entryId, title),
        // What the queue is doing to this card right now, if anything. A function so the
        // card tracks the state rather than receiving a snapshot at mount.
        captureOf: (entryId) => capturing[entryId] ?? null,
        remove: (entryId) => removeCardByEntry(entryId),
        // NO functions that read `nodes` here. A card $derived calling one subscribes that
        // card to the whole array — which xyflow writes every card's measurement back into
        // after every render. That circle is effect_update_depth_exceeded, three times now.
        // Nesting facts travel ON the node instead: sizeParents stamps data.nested and
        // data.childCount where nesting actually changes.
        resized: (entryId) => noteResize(entryId),
        // The roster, so a card can name whoever it is assigned to whether the assignment
        // arrived hydrated from the server or as raw ids on a live operation.
        peopleFrom: (value) => peopleFrom(value, members),
        unnest: (entryId) => unnest(entryId),
        ungroup: (entryId) => ungroup(entryId),
        toggleRunbookBranch: (nodeId) => toggleRunbookBranch(nodeId),
        focusRunbookNode: (nodeId) => focusRunbookRelationship(nodeId),
    });

    const nodeOfEntry = (entryId) =>
        nodes.find((n) => Number(n.data?.entryId) === Number(entryId));

    onMount(() => {
        // The baseline for every diff. Without it the first flush sends the whole board back
        // as a list of additions.
        lastSent = toServerGraph();

        listenForCaptures();
        joinPresence();

        // The composer's context strip can drop a card from the turn; the canvas has to agree,
        // or the selection it shows and the selection the AI gets are two different things.
        window.addEventListener("research-deselect", onDeselect);

        // The chat panel says "I'm typing" with a window event; this island whispers it to
        // the room. Throttled here as the single chokepoint, whatever the panel does.
        window.addEventListener("room-chat-typing", onChatTyping);
        window.addEventListener("research-map-visibility", onMapVisibility);
        window.addEventListener("resize", clampControlPanel);

        // Cards measure themselves AFTER the first render, so a parent sized at load time was
        // sized against defaults rather than its children's real dimensions. Re-fit once the
        // measurements exist, or a group opens too small and its contents sit outside it.
        setTimeout(
            () => {
                nodes = sizeParents(nodes);
                clampControlPanel();
                if (runbookFlow) {
                    tidy({
                        focusNodeId: runbookStartNodeId(nodes),
                        announce: false,
                        save: false,
                    });
                }
            },
            runbookFlow ? 550 : 150,
        );

        // Heartbeat + expiry. The goodbye below handles a clean exit; this handles every
        // OTHER exit — a killed tab, a dropped socket, a whisper that never flushed. Marks
        // are re-announced while their owner lives and expire when the announcements stop,
        // so a ghost mark is structurally impossible rather than dependent on one
        // member_removed event arriving.
        presenceTimer = setInterval(() => {
            announceWorking();
            sweepWorking();
        }, 15000);

        return () => {
            // The goodbye must be EXPLICIT, and it must actually LEAVE. Echo lives on the
            // page, not in this component — unmounting without leave() keeps the person a
            // presence member, so the others never get member_removed: their avatar stays,
            // and every card they were on stays marked as theirs. That is precisely the bug
            // this teardown once fixed before a rewrite dropped it.
            try {
                presence?.whisper?.("working", {
                    userId: Number(me?.id ?? 0),
                    nodeIds: [],
                });
            } catch (e) {
                /* socket already gone */
            }
            window.Echo?.leave?.("research-board-presence." + boardId);
            window.Echo?.leave?.("research-board." + boardId);
            window.Echo?.leave?.("chat-room.board." + boardId);
            heldStyleEl?.remove();
            heldStyleEl = null;
            clearInterval(presenceTimer);
            stopRunbookExpansion();
            window.removeEventListener("research-deselect", onDeselect);
            window.removeEventListener("room-chat-typing", onChatTyping);
            window.removeEventListener(
                "research-map-visibility",
                onMapVisibility,
            );
            window.removeEventListener("resize", clampControlPanel);
        };
    });

    function onDeselect(event) {
        const entryId = Number(
            event?.detail?.entryId ?? event?.detail?.[0]?.entryId ?? 0,
        );
        nodes = nodes.map((n) =>
            !entryId || Number(n.data?.entryId) === entryId
                ? { ...n, selected: false }
                : n,
        );
    }

    const { screenToFlowPosition, fitView } = useSvelteFlow();

    // Typed mind-map edges: colour + label per kind, cycled by clicking the edge.
    // Declared BEFORE the state initializers below — `toFlowEdge` (hoisted) closes over
    // these, and `.map(toFlowEdge)` runs at init, so a `const` declared later would be in
    // its temporal dead zone (TDZ crash the moment a board has any edges).
    // The research card types, in thinking order. Shown in the + Add menu and draggable
    // onto the canvas; anything else usable in the project follows under "Other content".
    const CARD_TYPES = [
        {
            handle: "research_topics",
            label: "Topic",
            icon: "💡",
            hint: "What you are working on",
        },
        {
            handle: "research_questions",
            label: "Question",
            icon: "❓",
            hint: "Something the research must answer",
        },
        {
            handle: "research_definitions",
            label: "Definition",
            icon: "📖",
            hint: "Pin a contested term to an authority + criteria",
        },
        {
            handle: "research_hypotheses",
            label: "Hypothesis",
            icon: "🔬",
            hint: "A candidate answer, to compete against the others",
        },
        {
            handle: "research_claims",
            label: "Claim",
            icon: "⚖️",
            hint: "An assertion to back with evidence",
        },
        {
            handle: "research_sources",
            label: "Source",
            icon: "📰",
            hint: "Evidence — or just paste a URL on the canvas",
        },
        {
            handle: "research_notes",
            label: "Note",
            icon: "📝",
            hint: "Your own thinking",
        },
        {
            handle: "research_groups",
            label: "Group",
            icon: "🗂️",
            hint: "A named, colour-coded container — drop cards into it",
        },
        {
            handle: "research_people",
            label: "Person",
            icon: "👤",
            hint: "A subject, witness, expert — or the side you still owe a reply to",
        },
        {
            handle: "research_events",
            label: "Event",
            icon: "📅",
            hint: "Something that happened on a date — builds the timeline",
        },
        {
            handle: "research_tasks",
            label: "Task",
            icon: "☑️",
            hint: "Work the research still owes",
        },
    ];

    // Evidence kinds — clicking a connection cycles through these.
    const KINDS = [
        "relates",
        "supports",
        "contradicts",
        "context",
        "cites",
        "answers",
        "flags",
        "example_of",
        "consistent",
        "inconsistent",
    ];
    // STRUCTURAL kinds: what a card IS to another (its topic, its definition, its rival).
    // Drawn faintly and NOT cycled — they're the plan's shape, not a claim about evidence.
    const STRUCTURAL = [
        "about",
        "defines_for",
        "alternative_to",
        "from_source",
        "parent_of",
        // Systems: what a component is to another component.
        "depends_on",
        "flows_to",
        "uses",
        "contains",
        "on_success",
        "on_failure",
    ];
    // Which kinds decide the SHAPE of a tidied canvas lives in ./layout.js — see EDGE_TIERS
    // there. (`contains` ranks like parent_of, but carries no kinship meaning: RelationshipTrace
    // keys strictly on parent_of, deliberately.)
    const KIND_COLOR = {
        relates: "#a1a1aa",
        supports: "#16a34a",
        contradicts: "#dc2626",
        context: "#0891b2",
        cites: "#71717a",
        answers: "#2563eb",
        flags: "#ea580c",
        example_of: "#8b5cf6",
        // Evidence ↔ hypothesis (the ACH matrix reads these).
        consistent: "#059669",
        inconsistent: "#b91c1c",
        about: "#a8a29e",
        defines_for: "#a8a29e",
        alternative_to: "#8b5cf6",
        from_source: "#a1a1aa",
        parent_of: "#6366f1",
        depends_on: "#e11d48",
        flows_to: "#0284c7",
        uses: "#7c3aed",
        contains: "#64748b",
        // The branch pair reads at a glance: green continues, red diverts.
        on_success: "#16a34a",
        on_failure: "#dc2626",
    };

    const localStateKey = untrack(() => stateKey || boardId || "default");
    const startingNodes = (initial.nodes ?? []).map(toFlowNode);
    const startingEdges = (initial.edges ?? []).map(toFlowEdge);
    let activeRunbookFingerprint = untrack(() => runbookFingerprint);
    let collapsedRunbookNodes = $state.raw(
        readRunbookCollapse(startingNodes, startingEdges),
    );
    let enabledRunbookRoles = $state.raw([...RUNBOOK_EDGE_ROLES]);
    let expansionVisibleIds = $state.raw(null);
    let runbookExpanding = $state(false);
    let branchExpansionNodeId = $state(null);
    let expansionFrame = null;
    let expansionLayoutTimer = null;
    /*
     * What each expansion pushed aside, as deltas per branch root. Collapsing that branch
     * slides the same groups back by the same amounts, so the connection to a displaced
     * neighbour shortens again. Deltas — never positions: restoring saved positions is the
     * old snapshot bug that snapped a re-tidied vertical flow back to horizontal. A tidy or
     * a structure change clears the ledger, because both re-resolve the space themselves.
     */
    const branchRoomShifts = new Map();

    function undoRoomShifts(rootIds) {
        let changed = false;
        for (const rootId of rootIds) {
            const records = branchRoomShifts.get(rootId);
            if (!records) continue;
            // Reverse order, so a cascade unwinds the way it wound.
            for (const record of [...records].reverse()) {
                nodes = nodes.map((node) => record.ids.includes(node.id)
                    ? {
                          ...node,
                          position: {
                              x: node.position.x - record.dx,
                              y: node.position.y - record.dy,
                          },
                      }
                    : node);
            }
            branchRoomShifts.delete(rootId);
            changed = true;
        }

        return changed;
    }
    let manuallyHiddenRunbookNodes = $state.raw(readMapVisibility());
    let dependencyFocusIds = $state.raw(null);
    const startingProjection = projectGraph(startingNodes, startingEdges);
    let nodes = $state.raw(sizeParents(startingProjection.nodes));
    let edges = $state.raw(startingProjection.edges);
    let runbookStats = $state({
        visible: startingProjection.visibleNodeCount,
        total: startingProjection.totalNodeCount,
    });
    const runbookPhaseFrames = $derived.by(() =>
        runbookFlow ? buildRunbookPhaseFrames(nodes, edges) : [],
    );
    let version = initialVersion;
    let saveTimer = null;
    let banner = $state(null); // {text, cls} — pin/upload feedback + errors

    /**
     * Live capture progress, keyed by entry id: { line, done }.
     *
     * Reading a page is slow and unpredictable, so the card lands immediately and the queue
     * reports on itself. Showing WHAT it's doing ("looking for an archived copy…") is the
     * difference between a wait that makes sense and one that looks broken.
     */
    let capturing = $state({});

    let selectMode = $state(false); // lasso multi-select vs pan
    let typeMenuOpen = $state(false); // "add any type" dropdown
    let menu = $state(null); // right-click context menu: { kind, x, y, ... }
    let dropHover = $state(false); // file / card drag-over highlight
    let selectedNodeIds = $state([]); // canvas node ids currently selected
    let draggingType = $state(null); // card type being dragged out of the + Add menu
    let renaming = $state(null); // { nodeId, entryId, value } — card renaming inline
    let proximity = $state(
        localStorage.getItem("zar_research_proximity") !== "0",
    ); // drag-near-to-link
    let edgeSeq = 0; // makes edge ids unique even within the same millisecond
    let retryingSave = false; // one merge-and-retry after a 409, never a loop
    let flowElement = $state();
    let controlPanelElement = $state();
    let controlPanelDrag = null;
    let controlPanel = $state(readControlPanel());

    function controlPanelKey() {
        return `zar-research-controls:${localStateKey}`;
    }

    function readControlPanel() {
        try {
            return parsePanelState(localStorage.getItem(controlPanelKey()));
        } catch (e) {
            return { x: 10, y: 10, minimized: false };
        }
    }

    function rememberControlPanel() {
        try {
            localStorage.setItem(
                controlPanelKey(),
                JSON.stringify({
                    x: controlPanel.x,
                    y: controlPanel.y,
                    minimized: controlPanel.minimized,
                }),
            );
        } catch (e) {
            /* private mode: the panel remains movable for this visit */
        }
    }

    function clampControlPanel(x = controlPanel.x, y = controlPanel.y) {
        if (!flowElement || !controlPanelElement) return;
        const flowRect = flowElement.getBoundingClientRect();
        const panelRect = controlPanelElement.getBoundingClientRect();
        const position = clampPanelPosition({ x, y }, flowRect, panelRect);

        controlPanel.x = position.x;
        controlPanel.y = position.y;
    }

    function startControlPanelDrag(event) {
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        controlPanelDrag = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            x: controlPanel.x,
            y: controlPanel.y,
        };
    }

    function moveControlPanel(event) {
        if (controlPanelDrag?.pointerId !== event.pointerId) return;
        if (!flowElement || !controlPanelElement) return;
        const position = draggedPanelPosition(
            { x: controlPanelDrag.x, y: controlPanelDrag.y },
            { x: controlPanelDrag.startX, y: controlPanelDrag.startY },
            { x: event.clientX, y: event.clientY },
            flowElement.getBoundingClientRect(),
            controlPanelElement.getBoundingClientRect(),
        );
        controlPanel.x = position.x;
        controlPanel.y = position.y;
    }

    function stopControlPanelDrag(event) {
        if (controlPanelDrag?.pointerId !== event.pointerId) return;
        controlPanelDrag = null;
        rememberControlPanel();
    }

    function closeControlPanelMenus() {
        typeMenuOpen = false;
        tidyMenu = false;
    }

    function closeControlPanelMenusOnOutsidePointer(event) {
        if (!typeMenuOpen && !tidyMenu) return;
        const menuArea = event.target?.closest?.(".rf-type-wrap, .rf-split");
        if (!menuArea) closeControlPanelMenus();
    }

    function toggleTypeMenu() {
        const open = !typeMenuOpen;
        closeControlPanelMenus();
        typeMenuOpen = open;
    }

    function toggleTidyMenu() {
        const open = !tidyMenu;
        closeControlPanelMenus();
        tidyMenu = open;
    }

    async function toggleControlPanel() {
        controlPanel.minimized = !controlPanel.minimized;
        closeControlPanelMenus();
        await tick();
        clampControlPanel();
        rememberControlPanel();
    }

    function readRunbookCollapse(nodeRows, edgeRows) {
        if (!runbookFlow) return new Set();
        const fallback = defaultRunbookCollapse(nodeRows, edgeRows);

        try {
            const saved = localStorage.getItem(
                `zar-runbook-collapse:v2:${localStateKey}`,
            );
            const restored = restoreRunbookCollapse(
                saved,
                activeRunbookFingerprint,
                fallback,
            );
            localStorage.setItem(
                `zar-runbook-collapse:v2:${localStateKey}`,
                JSON.stringify({
                    fingerprint: activeRunbookFingerprint,
                    ids: [...restored],
                }),
            );

            return restored;
        } catch (e) {
            return fallback;
        }
    }

    function rememberRunbookCollapse() {
        try {
            localStorage.setItem(
                `zar-runbook-collapse:v2:${localStateKey}`,
                JSON.stringify({
                    fingerprint: activeRunbookFingerprint,
                    ids: [...collapsedRunbookNodes],
                }),
            );
        } catch (e) {
            /* private mode: collapse remains useful for this visit */
        }
    }

    function projectGraph(nodeRows, edgeRows) {
        if (!runbookFlow) {
            return {
                nodes: nodeRows,
                edges: edgeRows,
                visibleNodeCount: nodeRows.length,
                totalNodeCount: nodeRows.length,
            };
        }

        const projected = projectRunbookGraph(
            nodeRows,
            edgeRows,
            collapsedRunbookNodes,
            enabledRunbookRoles,
            expansionVisibleIds,
            manuallyHiddenRunbookNodes,
            dependencyFocusIds,
        );

        return { ...projected, edges: projected.edges.map(presentRunbookEdge) };
    }

    function applyRunbookProjection(nodeRows = nodes, edgeRows = edges) {
        const projected = projectGraph(nodeRows, edgeRows);
        nodes = sizeParents(
            projected.nodes.map((node) => ({
                ...node,
                data: {
                    ...node.data,
                    runbookLoadingChildren: node.id === branchExpansionNodeId,
                },
            })),
        );
        edges = projected.edges;
        runbookStats = {
            visible: projected.visibleNodeCount,
            total: projected.totalNodeCount,
        };
    }

    function focusRunbookNode(nodeId, duration = 300) {
        if (!nodeId) return;
        setTimeout(() => {
            const node = nodes.find(
                (candidate) => candidate.id === nodeId && !candidate.hidden,
            );
            if (!node) return;
            /*
             * The node AND its visible children. An expanded phase of eight steps stands
             * taller than the screen, and fitting only the parent card at full zoom showed
             * one corner of the frame with every edge running off the viewport — which reads
             * as "the layout is broken", when what was broken was where we were looking.
             * Collapsed or childless nodes fit exactly as before.
             */
            const { children } = runbookHierarchy(edges);
            const branch = [node.id];
            const queue = [...(children.get(node.id) ?? [])];
            const visible = new Set(
                nodes.filter((item) => !item.hidden).map((item) => item.id),
            );
            while (queue.length) {
                const id = queue.shift();
                if (!id || branch.includes(id) || !visible.has(id)) continue;
                branch.push(id);
                queue.push(...(children.get(id) ?? []));
            }
            fitView({
                nodes: branch.map((id) => ({ id })),
                duration,
                maxZoom: 1.1,
                padding: branch.length > 1 ? 0.2 : 0.35,
            });
        }, 30);
    }

    /*
     * Expanding and collapsing move NOTHING but the branch itself. This used to auto-tidy —
     * asked for, built, and then withdrawn once it was lived with: a toggle that reshuffles
     * the whole drawing makes every click a surprise, and the old collapse restored a
     * position snapshot taken at expand time, so re-tidying the flow vertically while a
     * branch was open snapped the canvas back to horizontal on collapse. Now: collapse hides
     * the children and touches nothing else; expand reveals them and anchors them around
     * their parent, in the CURRENT tidy direction, one rank away so their connections stay
     * short. Rearranging the canvas is the Tidy button's job, on purpose, when asked.
     */
    function toggleRunbookBranch(nodeId) {
        if (!runbookFlow) return;
        const expanding = collapsedRunbookNodes.has(nodeId);
        stopRunbookExpansion();
        const next = new Set(collapsedRunbookNodes);
        if (!expanding) {
            next.add(nodeId);
            collapsedRunbookNodes = next;
            rememberRunbookCollapse();
            applyRunbookProjection();
            // The neighbours this branch pushed aside come back, so the connection to them
            // shortens again — and so do the records of any sub-branch being folded away
            // with it, or their pushes would linger with no collapse left to undo them.
            const canonical = edges.filter((edge) => !edge.data?.projection);
            const { children } = runbookHierarchy(canonical);
            const folded = [nodeId];
            const walk = [...(children.get(nodeId) ?? [])];
            while (walk.length) {
                const id = walk.shift();
                if (!id || folded.includes(id)) continue;
                folded.push(id);
                walk.push(...(children.get(id) ?? []));
            }
            undoRoomShifts(folded);
            nodes = sizeParents(nodes);
            edges = separateEdgeLabels(nodes, edges, tidyDir);
            scheduleSave();
            focusRunbookNode(nodeId, 400);

            return;
        }

        expansionVisibleIds = new Set(
            nodes.filter((node) => !node.hidden).map((node) => node.id),
        );
        branchExpansionNodeId = nodeId;
        next.delete(nodeId);
        collapsedRunbookNodes = next;
        runbookExpanding = true;
        rememberRunbookCollapse();
        applyRunbookProjection();
        scheduleRunbookExpansion();
    }

    function collapseRunbookToPhases() {
        stopRunbookExpansion();
        const canonical = edges.filter((edge) => !edge.data?.projection);
        const { children, parent } = runbookHierarchy(canonical);
        collapsedRunbookNodes = new Set(
            [...children.keys()].filter((id) => !parent.has(id)),
        );
        rememberRunbookCollapse();
        applyRunbookProjection();
        // Every push still on the ledger unwinds — this is the fold-everything action, so
        // there is no later collapse left to slide anything back.
        undoRoomShifts([...branchRoomShifts.keys()]);
        nodes = sizeParents(nodes);
        edges = separateEdgeLabels(nodes, edges, tidyDir);
        setTimeout(() => fitView({ duration: 400, padding: 0.2 }), 60);
    }

    function expandRunbook() {
        stopRunbookExpansion();
        expansionVisibleIds = new Set(
            nodes.filter((node) => !node.hidden).map((node) => node.id),
        );
        collapsedRunbookNodes = new Set();
        runbookExpanding = true;
        rememberRunbookCollapse();
        applyRunbookProjection();
        scheduleRunbookExpansion();
    }

    function scheduleRunbookExpansion() {
        // Leave one complete paint for the skeleton before any child cards mount.
        expansionFrame = window.requestAnimationFrame(() => {
            expansionFrame = window.requestAnimationFrame(expandRunbookBatch);
        });
    }

    function expandRunbookBatch() {
        const expanded = branchExpansionNodeId
            ? nextRunbookBranchExpansion(
                  nodes,
                  edges,
                  branchExpansionNodeId,
                  expansionVisibleIds,
                  24,
                  collapsedRunbookNodes,
              )
            : nextRunbookExpansion(nodes, expansionVisibleIds, 24);
        expansionVisibleIds = expanded.visible;
        applyRunbookProjection();

        if (!expanded.done) {
            expansionFrame = window.requestAnimationFrame(expandRunbookBatch);

            return;
        }

        const focusedNodeId = branchExpansionNodeId;
        expansionFrame = null;
        expansionVisibleIds = null;
        runbookExpanding = false;
        branchExpansionNodeId = null;
        applyRunbookProjection();
        scheduleExpandedRunbookLayout(focusedNodeId);
    }

    /*
     * Give the just-revealed children positions — and change nothing else.
     *
     * Their stored positions are wherever a past layout left them, which after any manual
     * tidy or drag can be half a canvas from where their parent stands now; revealed there,
     * their connections cross the whole drawing. So each branch is anchored at its parent —
     * the parent does not move — with the children one rank away in the CURRENT tidy
     * direction. No other group is touched, on purpose: this used to run a canvas-wide tidy,
     * and a toggle that rearranges the drawing makes every click a surprise. Rearranging is
     * the Tidy button's job. The delay stands: cards must mount and measure first, or the
     * anchoring is built from guessed sizes.
     */
    function scheduleExpandedRunbookLayout(nodeId = null) {
        clearTimeout(expansionLayoutTimer);
        expansionLayoutTimer = setTimeout(() => {
            expansionLayoutTimer = null;
            const space =
                TIDY_SPACING.find((sp) => sp.key === tidySpace) ??
                TIDY_SPACING[1];
            const canonical = edges.filter((edge) => !edge.data?.projection);
            const roots = nodeId
                ? [nodeId]
                : (() => {
                      // Expand-all: every open top-level group anchors its own children.
                      const { children, parent } = runbookHierarchy(canonical);

                      return [...children.keys()].filter(
                          (id) =>
                              !parent.has(id) &&
                              !collapsedRunbookNodes.has(id),
                      );
                  })();
            let laid = nodes;
            for (const root of roots) {
                laid = layoutRunbookBranch(
                    laid,
                    edges,
                    root,
                    tidyDir,
                    space,
                    tidyStyle,
                );
                // Whatever the opened branch now covers slides along the flow axis just far
                // enough to clear it — and a group not in the way never moves. The middle
                // ground between the two behaviours tried before this: a full tidy on every
                // toggle rearranged the drawing, and moving nothing opened children straight
                // over the collapsed groups beside their parent.
                const madeRoom = makeRoomForBranch(laid, edges, root, tidyDir);
                laid = madeRoom.nodes;
                if (madeRoom.displaced.length) {
                    branchRoomShifts.set(root, [
                        ...(branchRoomShifts.get(root) ?? []),
                        ...madeRoom.displaced,
                    ]);
                }
            }
            nodes = sizeParents(laid);
            edges = separateEdgeLabels(nodes, edges, tidyDir);
            scheduleSave();
            if (nodeId) {
                focusRunbookNode(nodeId, 400);
            } else {
                setTimeout(() => fitView({ duration: 400, padding: 0.2 }), 60);
            }
        }, 550);
    }

    function stopRunbookExpansion() {
        if (expansionFrame !== null)
            window.cancelAnimationFrame(expansionFrame);
        clearTimeout(expansionLayoutTimer);
        expansionFrame = null;
        expansionLayoutTimer = null;
        expansionVisibleIds = null;
        runbookExpanding = false;
        branchExpansionNodeId = null;
    }

    function readMapVisibility() {
        if (!runbookFlow) return new Set();
        try {
            return new Set(
                JSON.parse(
                    localStorage.getItem(`zar-runbook-hidden:${boardId}`) ??
                        "[]",
                ),
            );
        } catch (e) {
            return new Set();
        }
    }

    function onMapVisibility(event) {
        const nodeId = String(event?.detail?.nodeId ?? "");
        if (!nodeId) return;
        const next = new Set(manuallyHiddenRunbookNodes);
        event.detail.hidden ? next.add(nodeId) : next.delete(nodeId);
        manuallyHiddenRunbookNodes = next;
        applyRunbookProjection();
    }

    function focusRunbookDependencies(entryId) {
        stopRunbookExpansion();
        if (!entryId) {
            dependencyFocusIds = null;
            applyRunbookProjection();
            setTimeout(() => fitView({ duration: 300, padding: 0.18 }), 30);

            return;
        }

        const node = nodes.find(
            (candidate) => Number(candidate.data?.entryId) === Number(entryId),
        );
        if (!node) return;
        dependencyFocusIds = runbookDependencyNeighborhood(
            edges.filter((edge) => !edge.data?.projection),
            node.id,
        );
        applyRunbookProjection();
        setTimeout(() => fitView({ duration: 300, padding: 0.3 }), 30);
    }

    function toggleRunbookRole(role) {
        const next = new Set(enabledRunbookRoles);
        next.has(role) ? next.delete(role) : next.add(role);
        enabledRunbookRoles = [...next];
        applyRunbookProjection();
    }

    function revealRunbookNode(nodeId) {
        if (!runbookFlow) return;
        const { parent } = runbookHierarchy(edges);
        const next = new Set(collapsedRunbookNodes);
        let current = nodeId;
        let guard = 0;

        while (parent.has(current) && guard++ < 100) {
            current = parent.get(current);
            next.delete(current);
        }

        if (next.size !== collapsedRunbookNodes.size) {
            collapsedRunbookNodes = next;
            rememberRunbookCollapse();
            applyRunbookProjection();
        }
    }

    function focusRunbookRelationship(nodeId) {
        if (!nodeId) return;
        revealRunbookNode(nodeId);
        nodes = nodes.map((node) => ({
            ...node,
            selected: node.id === nodeId,
        }));
        focusRunbookNode(nodeId, 400);
    }

    // Which cards Livewire currently believes are on the workspace. The canvas persists over
    // fetch, so the Livewire side (project map, evidence panel) would otherwise keep showing
    // a card until the next full page load. Compared by ENTRY ids, so dragging a card around
    // never triggers a server re-render — only adding or removing one does.
    let notifiedCards = cardSignature();

    function cardSignature() {
        return nodes
            .map((n) => n.data?.entryId)
            .filter(Boolean)
            .sort((a, b) => a - b)
            .join(",");
    }

    /**
     * Who else is here, and what they are holding.
     *
     * Holding travels as a client event (a whisper) rather than through the server: it changes
     * on every drag start and stop, it is worthless a second later, and pushing it through a
     * request would put a round-trip in the middle of a drag. Reverb accepts client events from
     * channel members (config/reverb.php → accept_client_events_from).
     *
     * Deliberately NOT live cursors. They are the demo everyone asks for and the thing that
     * makes a busy board unreadable; "Ana is editing this card" carries the same information
     * without the noise.
     */
    function joinPresence() {
        if (!boardId || !window.Echo) return;

        const myId = Number(me?.id ?? 0);
        presence = window.Echo.join("research-board-presence." + boardId)
            .here((members) => {
                others = (members ?? []).filter((m) => Number(m.id) !== myId);
            })
            .joining((member) => {
                if (Number(member.id) === myId) return;
                others = [...others, member];
                onStatus?.(`${member.name ?? "Someone"} joined the workspace`);
                // Whispers are ephemeral — nothing replays them. Someone arriving would see an
                // empty board until the next time anybody changed selection, so everyone
                // already here re-announces what they're on.
                announceWorking();
            })
            .leaving((member) => {
                others = others.filter(
                    (m) => Number(m.id) !== Number(member.id),
                );
                // Leaving is SAID, and their marks leave with them — a card must never stay
                // flagged as belonging to somebody who is no longer here.
                onStatus?.(`${member.name ?? "Someone"} left the workspace`);
                const { [String(member.id)]: gone, ...rest } = workingByOthers;
                workingByOthers = rest;
                paintWorking();
            })
            // Somebody is typing in the team chat. The chat panel is Blade/Alpine and this
            // island owns the one socket subscription, so the signal is fanned out as a
            // window event rather than the panel opening a second subscription whose
            // lifecycle would have to agree with this one's.
            .listenForWhisper("typing", (e) => {
                if (String(e?.userId ?? "") === String(myId)) return;
                window.dispatchEvent(
                    new CustomEvent("room-chat-typing-seen", {
                        detail: { userId: e?.userId, name: e?.name },
                    }),
                );
            })
            .listenForWhisper("working", (e) => {
                const userId = String(e?.userId ?? "");
                if (!userId || userId === String(myId)) return;

                const nodeIds = (e?.nodeIds ?? []).filter(Boolean);
                if (nodeIds.length) {
                    // Stamped, so the sweeper can expire a mark whose owner stopped
                    // announcing — the exit path that never got to say goodbye.
                    workingByOthers = {
                        ...workingByOthers,
                        [userId]: {
                            nodeIds,
                            name: e.name,
                            color: e.color,
                            at: Date.now(),
                        },
                    };
                } else {
                    const { [userId]: gone, ...rest } = workingByOthers;
                    workingByOthers = rest;
                }
                paintWorking();
            });
    }

    /**
     * Tell the room which cards this person is on.
     *
     * Selection AND dragging, because both mean "I'm working on this" and the distinction is
     * invisible to the person watching: what they want to know is whether to touch that card.
     *
     * A whisper rather than a request. It changes on every click, it is worthless a second
     * later, and putting a round-trip in the middle of a drag to report it would be the wrong
     * trade in both directions. Reverb accepts client events from channel members.
     */
    function announceWorking() {
        const nodeIds = [...new Set([...selectedNodeIds, ...draggingIds])];
        presence?.whisper?.("working", {
            userId: Number(me?.id ?? 0),
            name: me?.name ?? "Someone",
            color: me?.color ?? "#6366f1",
            nodeIds,
        });
    }

    /**
     * Mark the cards other people are on — as a stylesheet, deliberately.
     *
     * Writing a class onto the node array would put a write inside an update, and this canvas
     * has produced effect_update_depth_exceeded from exactly that shape: a card that re-renders
     * remeasures, xyflow writes the measurement back into the array, and round it goes. Nodes
     * carry data-id in the DOM, so generated selectors do the same job and cannot participate
     * in the update cycle at all — including the initials, which are a `content` string on a
     * pseudo-element rather than an element anybody has to render.
     */
    let heldStyleEl = null;

    function paintWorking() {
        const rules = workingStyles(workingByOthers);

        if (!heldStyleEl) {
            heldStyleEl = document.createElement("style");
            heldStyleEl.dataset.rfWorking = "";
            document.head.appendChild(heldStyleEl);
        }
        if (heldStyleEl.textContent !== rules) heldStyleEl.textContent = rules;
    }

    /**
     * Fetch everything this canvas missed while it was away, and fold it in.
     *
     * Called by the ops listener when an arriving sequence number shows a GAP — applying out
     * of order would let a move land after the delete that removed its card and resurrect it.
     * This function was silently swallowed by a block rewrite once, and the damage profile is
     * worth recording: the listener's throw killed the handler, `seq` never advanced, so after
     * ONE dropped event every later event also looked like a gap and live updates stopped for
     * that client until reload. A missing catch-up isn't degraded — it's off.
     */
    async function catchUp() {
        if (!opsUrl) return;
        try {
            const res = await fetch(`${opsUrl}?since=${seq}`, {
                headers: { Accept: "application/json", "X-CSRF-TOKEN": csrf },
                redirect: "error",
            });
            if (!res.ok) return;
            const body = await res.json().catch(() => ({}));
            if (body.missed?.length) adoptRemoteOps(body.missed);
            if (typeof body.seq === "number") seq = Math.max(seq, body.seq);
        } catch (e) {
            // Offline. The next successful flush carries `missed` and recovers the same way.
        }
    }

    /** Listen for capture progress on this board. No Echo (or no board id) → no-op. */
    function listenForCaptures() {
        if (!boardId || !window.Echo) return;
        const channel = window.Echo.private("research-board." + boardId);
        // Chat moved to its own room channel when runbooks and live runs gained the same
        // conversation — one implementation, addressed by {type}.{id}. See Support\Collaboration\Room.
        const roomChannel = window.Echo.private("chat-room.board." + boardId);

        channel
            .stopListening(".source.capturing")
            .listen(".source.capturing", (e) => {
                if (!e?.entryId) return;
                capturing = {
                    ...capturing,
                    [e.entryId]: { line: e.line, done: false },
                };
            });

        /*
            Someone else's work, arriving as the same operations we send.

            Two rules make this safe. Applying OUT OF ORDER would let a move land after the
            delete that removed its card and quietly resurrect it, so a gap in the sequence is
            fetched before anything newer is applied. And a card under this user's pointer is
            theirs until they let go — adopting a remote position mid-drag makes the card jump
            out from under them, which reads as the app fighting them.
        */
        channel.stopListening(".board.ops").listen(".board.ops", (e) => {
            const at = Number(e?.seq ?? 0);
            const ops = e?.ops ?? [];
            if (!at || !ops.length) return;

            if (at <= seq) {
                return; // already have it — a redelivery, or our own work echoed back
            }

            // A gap means we missed something. Ask for it rather than guessing.
            if (at - ops.length > seq) {
                catchUp().then(() => {
                    adoptRemoteOps(ops);
                    seq = Math.max(seq, at);
                });

                return;
            }

            adoptRemoteOps(ops);
            seq = at;
        });

        // Someone said something. The socket carries the FACT; the panel re-reads the thread,
        // so a message is never quoted from a payload that could be stale or partial.
        roomChannel
            .stopListening(".room.message")
            .listen(".room.message", (e) => {
                // The entryId travels so the board can refresh the card's comment count on the
                // canvas; the words never do — the panel re-reads the thread.
                window.Livewire?.dispatch?.("board-message-arrived", {
                    entryId: e?.entryId ?? null,
                });
            });

        channel
            .stopListening(".source.captured")
            .listen(".source.captured", (e) => {
                if (!e?.entryId) return;
                const { [e.entryId]: _gone, ...rest } = capturing;
                capturing = rest;

                // The socket says WHAT happened; the card itself remains the authority on what the
                // page says, so re-read rather than trusting the payload for content.
                nodes = nodes.map((n) =>
                    Number(n.data?.entryId) === Number(e.entryId)
                        ? {
                              ...n,
                              data: {
                                  ...n.data,
                                  title: e.title || n.data.title,
                                  capture: e.state,
                              },
                          }
                        : n,
                );
                notifyCardsChanged();

                banner = e.warning
                    ? { text: e.warning, cls: "rf-warn" }
                    : { text: `Captured “${e.title}”.`, cls: "rf-info" };
                setTimeout(() => (banner = null), e.warning ? 14000 : 6000);
            });
    }

    /**
     * Take a card off the canvas.
     *
     * Off the CANVAS — the entry itself survives, exactly as pressing Delete has always done
     * here. The card is a view of a content record, and removing the view shouldn't destroy
     * the record; the workspace's own delete handles that with its own confirmation.
     */
    function removeCardByEntry(entryId) {
        const node = nodes.find(
            (n) => Number(n.data?.entryId) === Number(entryId),
        );
        if (!node) return;

        // Children would be orphaned onto a parent that no longer exists, and Svelte Flow
        // drops a node whose parent it can't find — so they come back to the canvas instead.
        nodes = sizeParents(
            nodes
                .filter((n) => n.id !== node.id)
                .map((n) =>
                    n.parentId === node.id
                        ? {
                              ...n,
                              parentId: undefined,
                              position: absolutePosition(n),
                          }
                        : n,
                ),
        );
        edges = edges.filter(
            (e) => e.source !== node.id && e.target !== node.id,
        );

        notifyCardsChanged();
        onStatus?.("Removed from the canvas");
        scheduleSave();
    }

    /**
     * Take one card out of whatever holds it, leaving it exactly where it looks.
     *
     * Nesting is easy to do by accident and was, until now, impossible to undo: a child's
     * position is relative to its parent, so releasing it without converting that position
     * back to canvas coordinates would fling it somewhere else on the board.
     */
    function unnest(entryId) {
        const node = nodeOfEntry(entryId);
        if (!node?.parentId || !canEdit) return;

        const at = absolutePosition(node);
        nodes = sizeParents(
            nodes.map((n) =>
                n.id === node.id
                    ? { ...n, parentId: undefined, position: at }
                    : n,
            ),
        );

        onStatus?.("Moved out");
        scheduleSave();
    }

    /**
     * Empty a card of its children — release them all to the canvas, keep the card itself.
     *
     * Deliberately NOT a delete. Dissolving a group people spent time filling is not something
     * to infer from "ungroup"; if they also want the group gone, the delete button is next to
     * this one and says so.
     */
    function ungroup(entryId) {
        const node = nodeOfEntry(entryId);
        if (!node || !canEdit) return;

        const held = nodes.filter((n) => n.parentId === node.id);
        if (held.length === 0) return;

        const freed = new Map(held.map((n) => [n.id, absolutePosition(n)]));
        nodes = sizeParents(
            nodes.map((n) =>
                freed.has(n.id)
                    ? { ...n, parentId: undefined, position: freed.get(n.id) }
                    : n,
            ),
        );

        onStatus?.(
            `Released ${held.length} card${held.length === 1 ? "" : "s"}`,
        );
        scheduleSave();
    }

    /**
     * The user just dragged a card's corner.
     *
     * Two things follow, and neither happened before. The size is now THEIRS, so the card is
     * marked to honour it — clipping its own content rather than growing past the box they
     * drew. And it gets saved: nothing else fires on a resize, so the size was lost on the
     * next load unless some other edit happened to trigger a save first.
     */
    function noteResize(entryId) {
        const node = nodeOfEntry(entryId);
        if (!node || !canEdit) return;

        nodes = sizeParents(
            nodes.map((n) =>
                n.id === node.id ? { ...n, class: "rf-sized" } : n,
            ),
        );
        scheduleSave();
    }

    function notifyCardsChanged() {
        const signature = cardSignature();
        if (signature === notifiedCards) return;
        notifiedCards = signature;
        window.Livewire?.dispatch?.("research-cards-changed");
    }

    // Receive nodes the Livewire side created (quote from selection, library pin, AI).
    function onLivewireReady() {
        window.Livewire?.on?.(
            "research-node-added",
            ({ node, version: fresh }) => {
                if (node) {
                    nodes = [...nodes, toFlowNode(node)];
                    applyRunbookProjection();
                }
                // That write moved the workspace's version on — adopt it, or this canvas's next
                // save is guarded by a stale one and comes back 409.
                if (fresh) version = fresh;
                // Livewire made this card, so its map is already fresh — just stay in step so
                // the next canvas save doesn't ask it to refresh for nothing.
                notifiedCards = cardSignature();
            },
        );
        // A card was edited/updated server-side → patch it in place (keep viewport + positions).
        window.Livewire?.on?.(
            "research-node-updated",
            ({
                entryId,
                title,
                reviewStatus,
                readiness,
                claimStatus,
                confidence,
                taskStatus,
                authoringStatus,
            }) => {
                nodes = nodes.map((n) => {
                    if (n.data.entryId !== entryId) return n;
                    const data = { ...n.data };
                    if (title !== undefined && title !== null)
                        data.title = title;
                    if (reviewStatus !== undefined)
                        data.reviewStatus = reviewStatus;
                    if (readiness !== undefined) data.readiness = readiness;
                    if (claimStatus !== undefined && claimStatus !== null)
                        data.claimStatus = claimStatus;
                    if (confidence !== undefined && confidence !== null)
                        data.confidence = confidence;
                    if (taskStatus !== undefined && taskStatus !== null)
                        data.taskStatus = taskStatus;
                    if (
                        authoringStatus !== undefined &&
                        authoringStatus !== null
                    )
                        data.authoringStatus = authoringStatus;
                    return { ...n, data };
                });
            },
        );
        // The project map (left panel) picked a card → centre it and select it on the canvas.
        window.Livewire?.on?.("research-focus-entry", ({ entryId }) => {
            let node = nodes.find((n) => n.data.entryId === entryId);
            if (!node) return;
            revealRunbookNode(node.id);
            node = nodes.find((n) => n.data.entryId === entryId);
            nodes = nodes.map((n) => ({ ...n, selected: n.id === node.id }));
            fitView({
                nodes: [{ id: node.id }],
                duration: 400,
                maxZoom: 1.1,
                padding: 0.35,
            });
        });
        window.Livewire?.on?.("research-focus-dependencies", ({ entryId }) => {
            focusRunbookDependencies(entryId);
        });
        // The AI (or a server reconcile) changed the graph → adopt it wholesale.
        window.Livewire?.on?.(
            "research-graph-reload",
            ({
                graph,
                version: fresh,
                runbookFingerprint: freshFingerprint,
            }) => {
                if (!graph) return;
                const structureChanged =
                    runbookFlow &&
                    freshFingerprint &&
                    freshFingerprint !== activeRunbookFingerprint;
                if (structureChanged) {
                    activeRunbookFingerprint = freshFingerprint;
                    branchRoomShifts.clear();
                    collapsedRunbookNodes = defaultRunbookCollapse(
                        (graph.nodes ?? []).map(toFlowNode),
                        (graph.edges ?? []).map(toFlowEdge),
                    );
                    rememberRunbookCollapse();
                }
                // Through sizeParents so a reloaded graph gets its group sizes AND its nesting
                // flags — the card toolbar reads those off the node, and a raw map leaves them
                // unset. Auto-arrange is deliberately gone: re-laying out the canvas from inside
                // an update handler is the write-triggered-by-update shape that has crashed this
                // canvas before, and Tidy is one click.
                applyRunbookProjection(
                    (graph.nodes ?? []).map(toFlowNode),
                    (graph.edges ?? []).map(toFlowEdge),
                );
                // Same guard problem as above: without the new version the next drag-save 409s.
                if (fresh) version = fresh;
                notifiedCards = cardSignature();
                if (structureChanged) {
                    clearTimeout(expansionLayoutTimer);
                    expansionLayoutTimer = setTimeout(() => {
                        expansionLayoutTimer = null;
                        tidy({
                            focusNodeId: runbookStartNodeId(nodes),
                            announce: false,
                            save: false,
                        });
                    }, 550);
                }
            },
        );
    }
    if (window.Livewire) onLivewireReady();
    else
        document.addEventListener("livewire:init", onLivewireReady, {
            once: true,
        });

    // Paste a URL anywhere on the canvas → scrape it into a source card (deduped).
    /** Is the paste going somewhere that takes text — a composer, a title field, an editor? */
    function pasteBelongsToSomethingElse(e) {
        const target = e.composedPath?.()[0] ?? e.target;
        const focused = document.activeElement;

        return [target, focused].some((el) =>
            el?.closest?.(
                'input, textarea, select, [contenteditable=""], [contenteditable="true"], .ProseMirror, .tox-edit-area',
            ),
        );
    }

    async function onPaste(e) {
        if (!canEdit || !pinUrl) return;

        // A paste belongs to whatever has focus. The canvas listens on the WINDOW so a
        // screenshot can be dropped in without clicking anything first — which also meant it
        // stole every paste into the assistant's composer and turned it into a note card.
        // The canvas only claims a paste when nothing else is taking text.
        if (pasteBelongsToSomethingElse(e)) return;
        const at = () =>
            screenToFlowPosition({
                x: e.clientX || window.innerWidth / 2,
                y: e.clientY || window.innerHeight / 2,
            });

        // A SCREENSHOT is the fastest thing anyone captures — cropped from a document, a
        // dashboard, a page that blocks readers — and it arrives on the clipboard, not as a
        // file. Requiring the user to save it first and then drag it in is the difference
        // between capturing a thought and abandoning it.
        const images = [...(e.clipboardData?.items ?? [])]
            .filter(
                (item) =>
                    item.kind === "file" && item.type.startsWith("image/"),
            )
            .map((item) => item.getAsFile())
            .filter(Boolean);
        if (images.length > 0 && attachUrl) {
            e.preventDefault();
            await uploadFiles(images, at());

            return;
        }

        const text = (e.clipboardData?.getData("text") || "").trim();
        if (text === "") return;

        // EVERY link in the paste, not just a lone one. Comparing coverage is the point:
        // eight write-ups of the same event, read together, is what shows which detail
        // everyone agrees on and which one headline invented.
        const urls = text.match(/https?:\/\/[^\s<>"')\]]+/gi) ?? [];
        if (urls.length > 0) {
            e.preventDefault();
            urls.length === 1
                ? await captureUrl(urls[0], at())
                : await captureUrls(urls, at());

            return;
        }

        // Anything else worth a card: a paragraph of notes, a quote, a transcript chunk.
        // It lands WITH its text — a note you have to retype after pasting isn't captured,
        // it's a chore. Short fragments are ignored so a stray copy doesn't litter the canvas.
        if (text.length < 25 || !cardsUrl) return;
        e.preventDefault();
        await dropNote(text, at());
    }

    /** Several links in one paste: placed as a grid, each read on the queue. */
    async function captureUrls(urls, at) {
        banner = { text: `Pinning ${urls.length} sources…`, cls: "rf-info" };
        try {
            const res = await fetch(pinUrl + "s", {
                method: "POST",
                redirect: "error",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-TOKEN": csrf,
                    Accept: "application/json",
                },
                body: JSON.stringify({ urls, position: at }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok || !body.ok) {
                banner = {
                    text:
                        body.error ||
                        `Could not pin those links (HTTP ${res.status}).`,
                    cls: "rf-warn",
                };
                return;
            }

            const fresh = (body.nodes ?? []).filter(
                (n) => !nodes.some((existing) => existing.id === n.id),
            );
            nodes = [...nodes, ...fresh.map(toFlowNode)];
            if (body.version) version = body.version;
            notifyCardsChanged();

            for (const node of body.nodes ?? []) {
                if (node.entryId)
                    capturing = {
                        ...capturing,
                        [node.entryId]: {
                            line: "Queued — reading the page…",
                            done: false,
                        },
                    };
            }

            const skipped = (body.rejected ?? []).length;
            banner = {
                text:
                    `Reading ${body.nodes.length} sources… you can keep working.` +
                    (skipped
                        ? ` ${skipped} link${skipped === 1 ? "" : "s"} skipped — not a public http(s) address.`
                        : ""),
                cls: skipped ? "rf-warn" : "rf-info",
            };
            setTimeout(() => (banner = null), skipped ? 12000 : 6000);
        } catch (err) {
            banner = {
                text: "Could not reach the server. Check your connection and try again.",
                cls: "rf-warn",
            };
        }
    }

    async function captureUrl(text, at) {
        banner = { text: "Capturing " + text + "…", cls: "rf-info" };
        try {
            // The server bounds its own capture; this is the outer guard so a proxy that
            // hangs up (or a request that outlives it) reports something true instead of
            // "offline", which sent people looking for a network problem that wasn't there.
            const abort = new AbortController();
            const giveUp = setTimeout(() => abort.abort(), 70000);
            const res = await fetch(pinUrl, {
                signal: abort.signal,
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-TOKEN": csrf,
                    Accept: "application/json",
                },
                body: JSON.stringify({ url: text, position: at }),
            });
            clearTimeout(giveUp);
            const body = await res.json();
            if (!body.ok) {
                banner = {
                    text: body.error || "Could not capture that URL.",
                    cls: "rf-warn",
                };
                return;
            }
            if (!nodes.some((n) => n.id === body.node.id))
                nodes = [...nodes, toFlowNode(body.node)];
            if (body.version) version = body.version;
            notifyCardsChanged();

            // The card is on the canvas; the page is being read on the queue. Show it working
            // rather than claiming a capture that hasn't happened yet.
            if (body.capture === "capturing" && body.node?.entryId) {
                capturing = {
                    ...capturing,
                    [body.node.entryId]: {
                        line: "Queued — reading the page…",
                        done: false,
                    },
                };
                banner = {
                    text: "Reading the page… you can keep working.",
                    cls: "rf-info",
                };
                setTimeout(() => (banner = null), 5000);
                return;
            }
            // A page that blocked us, or gave back nothing, still becomes a card — but say so
            // plainly and leave it up long enough to read. Silently pinning a bot-wall page as
            // a "source" is the failure that matters here: quotes would later be checked
            // against the site's refusal notice.
            banner = body.warning
                ? { text: body.warning, cls: "rf-warn" }
                : body.duplicate
                  ? {
                        text: `Captured — very similar to “${body.duplicate.title}” (${Math.round(body.duplicate.score * 100)}%).`,
                        cls: "rf-warn",
                    }
                  : {
                        text: body.created
                            ? "Source captured."
                            : "Source refreshed.",
                        cls: "rf-info",
                    };
            setTimeout(() => (banner = null), body.warning ? 14000 : 6000);
        } catch (err) {
            // Distinguish "the page took too long" from "we can't reach the app at all" —
            // they need completely different things from the user.
            banner =
                err?.name === "AbortError"
                    ? {
                          text: "That page took too long to read. It may be slow or blocking us — try again, or open it and paste the text onto a source card.",
                          cls: "rf-warn",
                      }
                    : {
                          text: "Could not reach the server. Check your connection and try again.",
                          cls: "rf-warn",
                      };
            setTimeout(() => (banner = null), 14000);
        }
    }

    // Server node → Svelte Flow node.
    //
    // Node data must stay structured-cloneable: xyflow probes it with structuredClone to
    // decide whether the array is deeply reactive, and anything it can't clone — a callback,
    // say — makes it warn about performance that isn't actually wrong. So the rename
    // callback goes through context (below) rather than riding along on the node.
    function toFlowNode(n) {
        const node = {
            id: n.id,
            type: "card",
            position: n.position ?? { x: 0, y: 0 },
            data: {
                ...(n.data ?? {}),
                cardType: n.cardType ?? "research_notes",
                entryId: n.entryId,
            },
        };
        // A card can live INSIDE another — the sub-node relationship. Its position is then
        // relative to the parent and it can't be dragged out of it by accident; pulling it
        // clear of the parent's bounds is the deliberate gesture that un-nests it.
        if (n.parentId) {
            node.parentId = n.parentId;
        }
        // A card you've resized keeps that size: a long note wants room, a label wants none,
        // and having to re-drag it on every load makes the canvas feel disposable.
        // Width has a sensible default; HEIGHT deliberately does not. A node with no height
        // measures its content, so a card grows to whatever it holds instead of clipping it.
        // Only a card the user resized gets a fixed height — and is marked, so the styles know
        // to honour that size rather than letting content push it open again.
        node.width = n.width || defaultWidth(n);
        if (n.height) {
            node.height = n.height;
            node.class = "rf-sized";
        }

        return node;
    }

    /**
     * How wide a card should open when nobody has sized it.
     *
     * Everything used to open at 220px, which is why a canvas read as rows of identical boxes
     * and a claim two sentences long arrived pre-truncated: the card was never given the room
     * its content needs, so the only way to READ a card was to resize it by hand. Width is the
     * lever that matters — height follows the content once the width is right, because text
     * wraps. A name needs less room than a paragraph, and a picture wants more than either.
     *
     * A rough, deterministic estimate is the right tool here. Measuring text properly means
     * laying it out first, and being 30px out on a card costs nothing; opening every card at
     * the same width costs the whole canvas its shape.
     */
    function defaultWidth(n) {
        const d = n.data ?? {};
        const type = n.cardType ?? d.cardType ?? "research_notes";

        // Containers hold other cards, so they start with room to drop things into. The real
        // size comes from sizeParents once they actually hold something.
        if (type === "research_groups") return 320;
        // A picture IS the content; a portrait sits beside a name and needs no extra room.
        if (d.mediaUrl && (d.isImage || d.isVideo) && !d.isPortrait) return 260;
        if (d.kind === "folder") return 250;

        // Everything else is text. Longer text gets a wider card rather than a taller thread of
        // three-word lines — roughly 30 characters per line at this font size.
        const text = [d.title, d.preview, d.excerpt].filter(Boolean).join(" ");
        const chars = text.length;

        if (chars <= 60) return 210;
        if (chars <= 160) return 250;
        if (chars <= 320) return 290;

        return 330;
    }

    // Server edge → Svelte Flow edge. Uses KINDS/KIND_COLOR (hoisted above the state init).
    // The fallback id carries the KIND: one card can both `supports` and `cites` another, and
    // an id of just source-target would collapse those two links into one.
    function toFlowEdge(e) {
        const known = KINDS.includes(e.kind) || STRUCTURAL.includes(e.kind);
        const kind = known ? e.kind : "relates";
        const structural = STRUCTURAL.includes(kind);

        const edge = {
            id: e.id ?? `${e.source}-${e.target}-${kind}`,
            type: "research",
            source: e.source,
            target: e.target,
            // Structure reads as plumbing: thin, dashed, behind the evidence lines.
            ...(structural
                ? {
                      style: `stroke:${KIND_COLOR[kind]};stroke-width:1.5;stroke-dasharray:3 3`,
                      label: kind.replaceAll("_", " "),
                  }
                : {}),
            // Which side of each card the line leaves/enters from, so a card with many
            // connections keeps them spread around it instead of stacked on one edge.
            sourceHandle: e.sourceHandle ?? null,
            targetHandle: e.targetHandle ?? null,
            label: kind !== "relates" ? kind : undefined,
            data: { ...(e.data ?? {}), kind },
            style: `stroke:${KIND_COLOR[kind]};stroke-width:2`,
            markerEnd: {
                type: MarkerType.ArrowClosed,
                color: KIND_COLOR[kind],
            },
        };

        return runbookFlow ? presentRunbookEdge(edge) : edge;
    }

    function presentRunbookEdge(edge) {
        const role = edge.data?.runbookRole ?? runbookEdgeRole(edge);
        if (!role) return edge;

        const appearances = {
            hierarchy: { color: "#64748b", label: "contains", dash: "4 5" },
            sequence: {
                color: "#0284c7",
                label: String(edge.id).includes("parallel")
                    ? "starts in parallel"
                    : "then",
            },
            dependency: {
                color: "#d97706",
                label: "required first",
                dash: "7 4",
            },
            success: { color: "#16a34a", label: "on success" },
            failure: { color: "#dc2626", label: "on failure" },
        };
        const appearance = appearances[role];
        const count = Number(edge.data?.count ?? 1);

        return {
            ...edge,
            label:
                count > 1 ? `${appearance.label} · ${count}` : appearance.label,
            data: { ...(edge.data ?? {}), runbookRole: role },
            style: `stroke:${appearance.color};stroke-width:${role === "failure" ? 2.6 : 2};${appearance.dash ? `stroke-dasharray:${appearance.dash}` : ""}`,
            markerEnd: {
                type: MarkerType.ArrowClosed,
                color: appearance.color,
            },
        };
    }

    function revealRunbookEdge(edge) {
        const role = runbookEdgeRole(edge);
        if (role && !enabledRunbookRoles.includes(role)) {
            enabledRunbookRoles = [...enabledRunbookRoles, role];
        }
        applyRunbookProjection();

        return role;
    }

    /**
     * Add one link, refusing the two cases that only ever produce clutter: a card linked to
     * itself, and a duplicate of a link that's already there. Different KINDS between the
     * same pair are fine — "supports" and "cites" are different statements.
     */
    function addEdgeOnce(
        source,
        target,
        kind = "relates",
        sourceHandle = null,
        targetHandle = null,
    ) {
        if (!source || !target || source === target) {
            return "invalid";
        }

        let resolvedKind = kind;
        let existing = null;
        let replace = null;
        if (runbookFlow) {
            ({
                kind: resolvedKind,
                existing,
                replace,
            } = resolveRunbookConnection(edges, source, target, kind));
        } else {
            existing = edges.find(
                (edge) =>
                    !edge.data?.projection &&
                    edge.source === source &&
                    edge.target === target &&
                    (edge.data?.kind ?? "relates") === resolvedKind,
            );
        }

        if (existing) {
            if (runbookFlow && existing.hidden) {
                revealRunbookEdge(existing);

                return "revealed";
            }

            return "existing";
        }

        const role = runbookFlow
            ? runbookEdgeRole({ data: { kind: resolvedKind } })
            : null;
        if (role && !enabledRunbookRoles.includes(role)) {
            enabledRunbookRoles = [...enabledRunbookRoles, role];
        }
        if (replace) edges = edges.filter((edge) => edge.id !== replace.id);
        edges = [
            ...edges,
            toFlowEdge({
                id: `e${edgeSeq++}-${Date.now()}`,
                source,
                target,
                kind: resolvedKind,
                sourceHandle: sourceHandle ?? replace?.sourceHandle ?? null,
                targetHandle: targetHandle ?? replace?.targetHandle ?? null,
            }),
        ];

        return replace ? "replaced" : "added";
    }

    // Click an edge → cycle its kind (relates → supports → … ) and re-save.
    // @xyflow/svelte passes a SINGLE { edge, event } object — NOT positional (event, edge).
    function onEdgeClick({ edge }) {
        if (!canEdit || !edge) return;
        if (edge.data?.projection) {
            onStatus?.("Expand the phase to edit this rolled-up connection");

            return;
        }
        // Structural links say what a card IS to another — cycling one into "supports" would
        // turn the plan's shape into a claim about evidence. Right-click to delete instead.
        if (STRUCTURAL.includes(edge.data?.kind)) {
            onStatus?.(
                `“${edge.data.kind.replaceAll("_", " ")}” is structure, not evidence — it isn't cycled`,
            );

            return;
        }
        const next =
            KINDS[
                (KINDS.indexOf(edge.data?.kind ?? "relates") + 1) % KINDS.length
            ];
        edges = edges.map((x) =>
            x.id === edge.id
                ? toFlowEdge({
                      id: x.id,
                      source: x.source,
                      target: x.target,
                      kind: next,
                  })
                : x,
        );
        scheduleSave();
    }

    /** The selection as last handled, so an unchanged re-fire does nothing at all. */
    let lastSelectionKey = "";

    /**
     * Tell Livewire — and the room — which cards are selected.
     *
     * THIS CALLBACK RUNS INSIDE XYFLOW'S OWN $EFFECT (SvelteFlow.svelte, "handle selection
     * change" — only the prop READ is untracked, the call is not). That makes two rules
     * non-negotiable, and both crashes at this seam broke one of them:
     *
     * - It must be IDEMPOTENT. It fired with a fresh array every flush, wrote it into
     *   $state `selectedNodeIds`, and announceWorking() read that state straight back —
     *   write-then-read of one signal inside one effect makes the effect depend on what it
     *   just changed, which is effect_update_depth_exceeded on an EMPTY canvas at mount.
     *   The dev-runtime stack named this exact line. The key guard means an unchanged
     *   selection returns before touching anything.
     * - The work happens in a MICROTASK, outside xyflow's tracking context, so nothing this
     *   reads can ever become that effect's dependency. The reverted highlight (applyFocus)
     *   died at this same seam for the same reason.
     */
    function onSelectionChange({ nodes: sel }) {
        const nodeIds = (sel ?? []).map((n) => n.id);
        const key = nodeIds.join("|");
        if (key === lastSelectionKey) {
            return;
        }
        lastSelectionKey = key;

        queueMicrotask(() => {
            selectedNodeIds = nodeIds;
            const ids = (sel ?? []).map((n) => n.data?.entryId).filter(Boolean);
            window.Livewire?.dispatch?.("research-selection", { ids });
            // Selection IS the "working on this" signal — dragging is the rare case.
            announceWorking();
        });
    }

    // Svelte Flow state → the server graph shape (positions, edges w/ kinds).
    function toServerGraph() {
        return {
            // Children must be saved AFTER their parents: Svelte Flow needs a parent to exist
            // before a node claiming it, and a graph reloaded in the wrong order silently
            // drops the nesting.
            nodes: [...nodes]
                .sort((a, b) => (a.parentId ? 1 : 0) - (b.parentId ? 1 : 0))
                .map((n) => ({
                    id: n.id,
                    entryId: n.data.entryId,
                    cardType: n.data.cardType,
                    position: { x: n.position.x, y: n.position.y },
                    parentId: n.parentId ?? null,
                    // Only a size the user CHOSE is saved. Persisting the automatic width or the
                    // measured height would freeze a card at whatever its content happened to be
                    // when it was last saved — so editing the note behind it would start clipping
                    // it, days later, for no visible reason. A width that still matches what the
                    // content would produce is this code's opinion, not the user's, so it isn't
                    // stored; the moment they drag the corner it stops matching and is.
                    width:
                        Math.round(n.width ?? 0) === defaultWidth(n)
                            ? null
                            : Math.round(n.width ?? 0) || null,
                    height: Math.round(n.height ?? 0) || null,
                    data: { title: n.data.title },
                })),
            // The proximity preview is a drag artefact, never part of the saved graph.
            edges: edges
                .filter((e) => !e.data?.temp && !e.data?.projection)
                .map((e) => ({
                    id: e.id,
                    source: e.source,
                    target: e.target,
                    kind: e.data?.kind ?? "relates",
                    sourceHandle: e.sourceHandle ?? null,
                    targetHandle: e.targetHandle ?? null,
                })),
        };
    }

    /**
     * Is the save endpoint the one we were given, or the page we're standing on?
     *
     * `fetch()` resolves a missing or relative URL against the current document, so a saveUrl
     * that didn't arrive turns every drag into a PUT at the board PAGE — which answers 405 and
     * says nothing about why. Seven of those in a row is what a stale view cache or a
     * half-deployed build looks like from the browser, and it's indistinguishable from a bug
     * in the canvas unless we name it.
     */
    function saveEndpointLooksWrong() {
        if (!opsUrl) return "The canvas wasn’t given a save address.";
        try {
            const target = new URL(opsUrl, window.location.href);
            if (target.pathname === window.location.pathname) {
                return "The canvas was given the page’s own address to save to.";
            }
            if (!target.pathname.endsWith("/ops")) {
                return `The canvas was given an unexpected save address (${target.pathname}).`;
            }
        } catch {
            return "The canvas was given an unusable save address.";
        }
        return null;
    }

    let saveBlocked = false;
    let networkFailures = 0;
    /** The graph as the server last confirmed it — the other side of every diff. */
    let lastSent = { nodes: [], edges: [], viewport: null };
    /** Our high-water mark in the board's op log. */
    let seq = Number(initialSeq ?? 0);
    /** Cards under the pointer right now; remote moves for these wait until the drag ends. */
    let draggingIds = new Set();

    /** When this person's typing was last whispered, so keystrokes don't each cost a frame. */
    let lastTypingWhisper = 0;

    function onChatTyping() {
        const now = Date.now();
        if (now - lastTypingWhisper < 1500) return;
        lastTypingWhisper = now;
        presence?.whisper?.("typing", {
            userId: Number(me?.id ?? 0),
            name: me?.name ?? "Someone",
        });
    }

    /** Heartbeat + expiry timer for the working marks. */
    let presenceTimer = null;

    /** Age out marks whose owner stopped announcing, and repaint if anything went. */
    function sweepWorking() {
        const { next, changed } = sweepStale(
            workingByOthers,
            Date.now(),
            45000,
        );
        if (changed) {
            workingByOthers = next;
            paintWorking();
        }
    }

    /** Everyone else on this workspace, and the cards each of them is working on. */
    let others = $state([]);
    let workingByOthers = {};
    let presence = null;

    function scheduleSave() {
        if (!canEdit || saveBlocked) return;

        // A sandbox has no save address ON PURPOSE, so the missing-endpoint warning below
        // would be wrong twice over: it isn't a misconfiguration, and telling a visitor to
        // reload because their changes aren't saved is the opposite of the invitation.
        if (sandbox) return;

        const wrong = saveEndpointLooksWrong();
        if (wrong) {
            // Say it ONCE and stop. Retrying can't fix a bad address, and a loop of failing
            // requests buries the one message that would have explained it.
            saveBlocked = true;
            banner = {
                text: `${wrong} Your changes aren’t being saved — reload the page. If it keeps happening, the server’s view/route cache is stale from a deploy.`,
                cls: "rf-warn",
            };
            onStatus?.("Not saving — see the message above");
            return;
        }

        clearTimeout(saveTimer);
        saveTimer = setTimeout(save, 800);
    }

    /**
     * Send what the user DID, and take what they missed.
     *
     * The ops are derived by diffing the last graph we successfully sent against the current
     * one, rather than being emitted at each of the fourteen places that change the canvas.
     * Hand-emitting reads better and fails worse: the first call site somebody adds without
     * remembering to emit an op is a change that silently never saves. A diff cannot be
     * forgotten, and it coalesces a drag's sixty position updates into one move for free.
     */
    async function save() {
        const current = toServerGraph();
        const ops = diffGraph(lastSent, current);
        const viewportChanged =
            JSON.stringify(current.viewport ?? null) !==
            JSON.stringify(lastSent.viewport ?? null);

        if (ops.length === 0 && !viewportChanged) {
            return;
        }

        onStatus?.("Saving…");
        try {
            const res = await fetch(opsUrl, {
                method: "POST",
                // A save must land on the endpoint we asked for. Following a redirect turns a
                // misrouted save into a POST at whatever it points to — in practice the board
                // PAGE, which answers 405 and reports the FINAL url, hiding the redirect.
                redirect: "error",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-TOKEN": csrf,
                    Accept: "application/json",
                },
                body: JSON.stringify({
                    since: seq,
                    ops,
                    viewport: current.viewport ?? null,
                }),
            });

            // An HTTP error is NOT a network failure, and retrying can't fix it. Reading the
            // body as JSON first would throw on an error page's HTML and land in the catch
            // below, which retries — that loop is what turned one bad address into an endless
            // stream of failing requests.
            if (!res.ok) {
                saveBlocked = true;
                clearTimeout(saveTimer);
                const said = await res.json().catch(() => null);
                if (res.status === 419) {
                    banner = {
                        text: "Your session expired, so the canvas can’t save. Reload the page — your last saved layout is safe.",
                        cls: "rf-warn",
                    };
                    onStatus?.("Session expired — reload to keep editing");

                    return;
                }
                banner = {
                    text:
                        said?.error || said?.message
                            ? `${said.error || said.message} Your canvas changes aren’t being saved.`
                            : `The server refused to save the canvas (HTTP ${res.status}). Your changes aren’t being stored — reload the page. If it persists, the deploy’s route/view cache is stale.`,
                    cls: "rf-warn",
                };
                onStatus?.("Not saving — see the message above");

                return;
            }

            const body = await res.json().catch(() => ({}));
            networkFailures = 0;

            // Someone else's work, arriving as the same ops we send. Applied to the LIVE
            // arrays, then folded into lastSent, or the next diff would try to undo them.
            if (body.missed?.length) {
                adoptRemoteOps(body.missed);
            }

            if (body.rejected?.length) {
                // Refusals are shown, never swallowed: a change that silently snaps back is
                // indistinguishable from a bug.
                banner = {
                    text:
                        body.rejected[0].reason ?? "Some changes were refused.",
                    cls: "rf-warn",
                };
                setTimeout(() => (banner = null), 8000);
            }

            if (typeof body.seq === "number") seq = body.seq;
            lastSent = toServerGraph();
            onStatus?.("Saved");
        } catch (e) {
            if (saveBlocked) return;
            // A genuine network blip: retry, but only a few times, so a laptop that went to
            // sleep doesn't wake to a request storm.
            if (++networkFailures <= 3) {
                onStatus?.("Offline — retrying");
                scheduleSave();

                return;
            }
            onStatus?.("Offline — changes not saved");
        }
    }

    /**
     * Fold other people's operations into this canvas.
     *
     * Applied through the same pure reducer the tests cover, on the SERVER shape, then mapped
     * back into flow nodes — so a remote change and a local one produce identical state, and
     * `lastSent` moves with it so the next diff doesn't try to revert what someone else did.
     */
    function adoptRemoteOps(remoteOps) {
        // Applied to the LIVE nodes, not to a server-shaped copy of them.
        //
        // toServerGraph() deliberately sends only what the server stores — a node's data is
        // trimmed to its title — so rebuilding the canvas from it would have stripped every
        // hydrated field off every card on any remote change: badges, previews, portraits,
        // comment counts, who a card is assigned to. The reducer works on either shape,
        // because all it touches is position, nesting, size and a shallow data merge.
        const applied = applyOps(
            nodes,
            edges.filter((edge) => !edge.data?.projection),
            remoteOps,
        );
        if (!applied.changed) {
            return;
        }

        // Positions of cards the user is dragging RIGHT NOW are theirs until they let go —
        // otherwise the card jumps out from under the pointer, which reads as the app fighting
        // them. Everything else adopts the remote state.
        const held = new Set(draggingIds);
        const mine = new Map(nodes.map((n) => [n.id, n]));

        const nextNodes = applied.nodes.map((n) => {
            const local = mine.get(n.id);
            // A card that arrived on a remote node.add is server-shaped and has to be built;
            // one we already had is already a flow node and must be left as it is.
            const node = local ? n : toFlowNode(n);

            return held.has(n.id) && local
                ? { ...node, position: local.position }
                : node;
        });
        const nextEdges = applied.edges.map((e) =>
            e.data?.kind ? e : toFlowEdge(e),
        );
        applyRunbookProjection(nextNodes, nextEdges);

        lastSent = toServerGraph();
        notifyCardsChanged();
    }

    // ── New cards: placed, then named IN PLACE on the canvas (no modal, no dialog) ──

    /** Click a type in + Add → drop it in the middle of the current view and name it. */
    function addCard(type, label = null, at = null) {
        if (!canEdit || !cardsUrl) return;
        typeMenuOpen = false;
        closeMenu();
        const where =
            at ??
            screenToFlowPosition({
                x: window.innerWidth / 2,
                y: window.innerHeight / 2,
            });
        dropCard(
            type,
            label ?? type.replace("research_", "").replace(/s$/, ""),
            where,
        );
    }

    /** POST a new card and add its node. Returns the node, or null when the save failed. */
    // NB: the text parameter is `cardBody`, NOT `body` — the response is destructured into a
    // local `body` further down, and a same-scope `const body` puts the parameter in the
    // temporal dead zone. That threw on every add, and the catch below swallowed it, so cards
    // silently stopped appearing.
    async function createCard(type, title, at, cardBody = null, kind = null) {
        onStatus?.("Adding…");
        try {
            const res = await fetch(cardsUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-TOKEN": csrf,
                    Accept: "application/json",
                },
                body: JSON.stringify({
                    type,
                    title,
                    position: at,
                    version,
                    body: cardBody,
                    kind,
                }),
            });
            const body = await res.json();
            if (body.ok) {
                const node = toFlowNode(body.node);
                nodes = [...nodes, node];
                if (body.version) version = body.version;
                onStatus?.("Saved");
                notifyCardsChanged();
                lastSent = toServerGraph();

                return node;
            }
            if (body.stale) {
                nodes = (body.graph.nodes ?? []).map(toFlowNode);
                edges = (body.graph.edges ?? []).map(toFlowEdge);
                if (body.version) version = body.version;
                onStatus?.("Updated elsewhere — try again");
            } else {
                onStatus?.(body.error || "Could not add card");
            }
        } catch (e) {
            // A network failure and a bug in this function looked identical here, which is how
            // a ReferenceError shipped as "cards silently stop appearing". Report it properly
            // and leave it in the console for whoever has to find it next.
            console.error("Adding a card failed", e);
            banner = {
                text:
                    "Could not add the card — " +
                    (e?.message || "unknown error") +
                    ".",
                cls: "rf-warn",
            };
            setTimeout(() => (banner = null), 8000);
            onStatus?.("Could not add card");
        }

        return null;
    }

    /** Commit an inline rename: save server-side, patch the card, leave edit mode. */
    function commitRename(entryId, title) {
        const clean = (title ?? "").trim();
        renaming = null;
        nodes = nodes.map((n) =>
            n.data.entryId === entryId
                ? { ...n, data: { ...n.data, editing: false } }
                : n,
        );
        if (!clean) return;
        window.Livewire?.dispatch?.("research-rename-card", {
            entryId,
            title: clean,
        });
        nodes = nodes.map((n) =>
            n.data.entryId === entryId
                ? { ...n, data: { ...n.data, title: clean } }
                : n,
        );
    }

    function handleConnect(connection) {
        if (!canEdit) return;
        const result = addEdgeOnce(
            connection.source,
            connection.target,
            "relates",
            connection.sourceHandle,
            connection.targetHandle,
        );
        if (result === "invalid" || result === "existing") {
            onStatus?.(
                result === "invalid"
                    ? "A card can’t link to itself"
                    : "Those are already linked",
            );

            return;
        }
        if (result === "revealed") {
            onStatus?.("Connection already existed — it is visible now");

            return;
        }
        if (result === "replaced") {
            onStatus?.("Connected as runbook sequence");
        }
        scheduleSave();
    }

    // ── Proximity linking: drag a card near another and the link forms on drop ───
    // Measured as the GAP between the two cards, not between their centres: a card is 220px
    // wide, so centre-distance never gets small unless they badly overlap. This matches what
    // the gesture actually means — "drop it next to that one".
    const PROXIMITY_GAP = 80;

    const nodeRect = (n) => ({
        x: n.position.x,
        y: n.position.y,
        w: n.measured?.width ?? 220,
        h: n.measured?.height ?? 130,
    });

    const nodeCentre = (n) => {
        const r = nodeRect(n);

        return { x: r.x + r.w / 2, y: r.y + r.h / 2 };
    };

    /** Shortest distance between two cards' edges — 0 when they overlap. */
    function gapBetween(a, b) {
        const ra = nodeRect(a);
        const rb = nodeRect(b);
        const dx = Math.max(
            0,
            Math.max(rb.x - (ra.x + ra.w), ra.x - (rb.x + rb.w)),
        );
        const dy = Math.max(
            0,
            Math.max(rb.y - (ra.y + ra.h), ra.y - (rb.y + rb.h)),
        );

        return Math.hypot(dx, dy);
    }

    /** The nearest OTHER card within range that isn't linked to this one already. */
    function nearestNode(node) {
        let best = null;
        let bestDistance = PROXIMITY_GAP;

        for (const other of nodes) {
            if (other.hidden || other.id === node.id) continue;
            const distance = gapBetween(node, other);
            if (distance < bestDistance) {
                bestDistance = distance;
                best = other;
            }
        }
        if (!best) return null;

        // Already connected either way → nothing to suggest.
        const linked = edges.some(
            (e) =>
                !e.data?.temp &&
                ((e.source === node.id && e.target === best.id) ||
                    (e.source === best.id && e.target === node.id)),
        );

        return linked ? null : best;
    }

    /** The dragged card and its neighbour, ordered left-to-right so the arrow reads naturally. */
    function proximityPair(node, near) {
        return nodeCentre(node).x < nodeCentre(near).x
            ? [node.id, near.id]
            : [near.id, node.id];
    }

    // NOTE: drag events hand over `{ targetNode, nodes, event }` — NOT `{ node }` like click
    // and context-menu events do. Destructuring `node` here silently yields undefined, which
    // is exactly how this feature first shipped doing nothing at all; accept either key so a
    // rename upstream degrades to working rather than to silence.
    function onNodeDrag({ targetNode, node: dragged }) {
        // Held cards belong to the person holding them until they let go.
        if (dragged?.id && !draggingIds.has(dragged.id)) {
            draggingIds.add(dragged.id);
            announceWorking();
        }
        const node = targetNode ?? dragged;
        if (!canEdit || !proximity || !node) return;
        const near = nearestNode(node);
        const live = edges.filter((e) => !e.data?.temp);
        if (!near) {
            if (live.length !== edges.length) edges = live;

            return;
        }
        // A dashed preview that follows the drag; it only becomes real on drop.
        const [source, target] = proximityPair(node, near);
        edges = [
            ...live,
            {
                id: "proximity-preview",
                source,
                target,
                data: { kind: "relates", temp: true },
                style: `stroke:${KIND_COLOR.relates};stroke-width:2;stroke-dasharray:6 4`,
                animated: true,
            },
        ];
    }

    function onNodeDragStop({ targetNode, node: dragged } = {}) {
        if (dragged?.id) {
            draggingIds.delete(dragged.id);
            announceWorking();
        }
        const moved = targetNode ?? dragged;

        // Dropping a card ONTO another puts it inside — the sub-node gesture. Checked before
        // the proximity link, because a card released over another means "belongs to this",
        // not "is near this", and doing both would be two relationships from one drop.
        if (moved && canEdit && nestOnDrop(moved)) {
            edges = edges.filter((e) => !e.data?.temp);
            scheduleSave();

            return;
        }

        const preview = edges.find((e) => e.data?.temp);
        edges = edges.filter((e) => !e.data?.temp);
        if (preview && canEdit && proximity) {
            const result = addEdgeOnce(
                preview.source,
                preview.target,
                "relates",
            );
            if (result === "added" || result === "replaced") {
                onStatus?.(
                    runbookFlow
                        ? "Connected in sequence"
                        : "Linked — click the line to set what kind",
                );
            } else if (result === "revealed") {
                onStatus?.("Connection already existed — it is visible now");
            }
        }
        scheduleSave();
    }

    /** Absolute canvas position of a node, walking up through any parents. */
    function absolutePosition(node) {
        let { x, y } = node.position;
        let parent = node.parentId
            ? nodes.find((n) => n.id === node.parentId)
            : null;
        let guard = 0;
        while (parent && guard++ < 10) {
            x += parent.position.x;
            y += parent.position.y;
            parent = parent.parentId
                ? nodes.find((n) => n.id === parent.parentId)
                : null;
        }

        return { x, y };
    }

    /** Would making `child` a child of `candidate` create a loop? */
    function wouldCycle(childId, candidateId) {
        let cursor = nodes.find((n) => n.id === candidateId);
        let guard = 0;
        while (cursor && guard++ < 20) {
            if (cursor.id === childId) return true;
            cursor = cursor.parentId
                ? nodes.find((n) => n.id === cursor.parentId)
                : null;
        }

        return false;
    }

    /**
     * Grow a parent so its children actually fit inside it.
     *
     * Svelte Flow positions a child relative to its parent but doesn't resize the parent, so
     * without this the children sit ON TOP of the card that owns them and the nesting is
     * invisible. Recomputed after every change rather than stored, so dragging a child around
     * inside its parent keeps the box honest.
     */

    /**
     * Nest a dropped card into whatever it was released over, or pull it out when it was
     * dragged clear of its parent. Returns true when the nesting changed.
     */
    function nestOnDrop(moved) {
        const node = nodes.find((n) => n.id === moved.id);
        if (!node) return false;

        const at = absolutePosition(node);
        const centre = {
            x: at.x + (node.measured?.width ?? 220) / 2,
            y: at.y + (node.measured?.height ?? 130) / 2,
        };

        // The deepest card whose bounds contain this one's centre, ignoring itself, its own
        // descendants (which would make a loop) and anything it's already inside.
        const host = nodes
            .filter(
                (other) =>
                    other.id !== node.id && !wouldCycle(node.id, other.id),
            )
            .filter((other) => {
                const pos = absolutePosition(other);
                const w = other.measured?.width ?? 220;
                const h = other.measured?.height ?? 130;

                return (
                    centre.x >= pos.x &&
                    centre.x <= pos.x + w &&
                    centre.y >= pos.y &&
                    centre.y <= pos.y + h
                );
            })
            .sort((a, b) => (b.parentId ? 1 : 0) - (a.parentId ? 1 : 0))[0];

        if (host && host.id !== node.parentId) {
            const hostAt = absolutePosition(host);
            nodes = sizeParents(
                nodes.map((n) =>
                    n.id === node.id
                        ? {
                              ...n,
                              parentId: host.id,
                              position: {
                                  x: Math.max(12, at.x - hostAt.x),
                                  y: Math.max(44, at.y - hostAt.y),
                              },
                          }
                        : n,
                ),
            );
            onStatus?.(`Nested inside “${host.data.title ?? "card"}”`);

            return true;
        }

        // Released outside its parent: promote it back to the canvas, keeping where it looks.
        if (!host && node.parentId) {
            nodes = sizeParents(
                nodes.map((n) =>
                    n.id === node.id
                        ? { ...n, parentId: undefined, position: at }
                        : n,
                ),
            );
            onStatus?.("Moved out");

            return true;
        }

        return false;
    }

    function toggleProximity() {
        proximity = !proximity;
        localStorage.setItem("zar_research_proximity", proximity ? "1" : "0");
        if (!proximity) edges = edges.filter((e) => !e.data?.temp);
    }

    // ── Right-click context menu ─────────────────────────────────────────────────
    function openNodeMenu({ node, event }) {
        if (!canEdit) return;
        event.preventDefault();
        const multi =
            selectedNodeIds.length > 1 && selectedNodeIds.includes(node.id);
        menu = {
            kind: multi ? "selection" : "node",
            x: event.clientX,
            y: event.clientY,
            nodeId: node.id,
            cardType: node.data?.cardType,
            entryId: node.data?.entryId,
            nested: !!node.parentId,
            holds: nodes.some((n) => n.parentId === node.id),
        };
    }
    function openEdgeMenu({ edge, event }) {
        if (!canEdit) return;
        event.preventDefault();
        if (edge.data?.projection) {
            onStatus?.("Expand the phase to edit this rolled-up connection");

            return;
        }
        menu = {
            kind: "edge",
            x: event.clientX,
            y: event.clientY,
            edgeId: edge.id,
            edgeKind: edge.data?.kind ?? "relates",
        };
    }
    function openPaneMenu({ event }) {
        if (!canEdit) return;
        event.preventDefault();
        menu = {
            kind: "pane",
            x: event.clientX,
            y: event.clientY,
            flow: screenToFlowPosition({ x: event.clientX, y: event.clientY }),
        };
    }
    const closeMenu = () => (menu = null);

    function openReader(entryId) {
        if (entryId)
            window.Livewire?.dispatch?.("research-open-card", { entryId });
        closeMenu();
    }

    function setCardStatus(entryId, field, value) {
        window.Livewire?.dispatch?.("research-card-status", {
            entryId,
            field,
            value,
        });
        // `status` only reaches here from a task card's menu → the task badge.
        const key =
            field === "review_status"
                ? "reviewStatus"
                : field === "readiness"
                  ? "readiness"
                  : field === "status"
                    ? "taskStatus"
                    : null;
        if (key)
            nodes = nodes.map((n) =>
                n.data.entryId === entryId
                    ? { ...n, data: { ...n.data, [key]: value } }
                    : n,
            );
        closeMenu();
    }

    function deleteNodes(ids) {
        const set = new Set(ids);
        nodes = nodes.filter((n) => !set.has(n.id));
        edges = edges.filter((e) => !set.has(e.source) && !set.has(e.target));
        scheduleSave();
        closeMenu();
    }

    function connectSelected() {
        const ids = selectedNodeIds;
        for (let i = 0; i < ids.length - 1; i++) {
            addEdgeOnce(ids[i], ids[i + 1], "relates");
        }
        scheduleSave();
        closeMenu();
    }

    /** Fan the selection into (or out of) the card that was right-clicked. */
    function connectSelectedTo(hubId, inbound) {
        for (const id of selectedNodeIds) {
            inbound
                ? addEdgeOnce(id, hubId, "relates")
                : addEdgeOnce(hubId, id, "relates");
        }
        scheduleSave();
        closeMenu();
    }

    /**
     * Set a connection's kind from the menu.
     *
     * Clicking a line cycles the EVIDENCE kinds only, on purpose — structural and systems
     * kinds are the diagram's shape, not a claim, and cycling through them by accident would
     * turn "depends on" into "supports" with one misplaced click. Without this menu they
     * would be reachable only through the assistant.
     */
    function setEdgeKind(id, kind) {
        const edge = edges.find((e) => e.id === id);
        if (!edge || edge.data?.kind === kind) {
            closeMenu();

            return;
        }
        edges = edges.map((e) =>
            e.id === id
                ? toFlowEdge({
                      id: e.id,
                      source: e.source,
                      target: e.target,
                      kind,
                      sourceHandle: e.sourceHandle,
                      targetHandle: e.targetHandle,
                  })
                : e,
        );
        scheduleSave();
        closeMenu();
    }

    function deleteEdge(id) {
        edges = edges.filter((e) => e.id !== id);
        scheduleSave();
        closeMenu();
    }

    // ── Tidy: dagre auto-layout ──────────────────────────────────────────────────
    /*
        Which way the graph flows, and how much air it gets.

        One button meant one shape, and the right shape depends on the board: a family tree or
        an org chart reads top-down, a dependency map or a process reads left-to-right, and a
        dense canvas needs room a sparse one wastes. Both choices persist per browser — someone
        who thinks left-to-right thinks that way on every board.
    */
    const TIDY_DIRECTIONS = [
        { key: "TB", glyph: "↓", label: "Top to bottom" },
        { key: "LR", glyph: "→", label: "Left to right" },
        { key: "BT", glyph: "↑", label: "Bottom to top" },
        { key: "RL", glyph: "←", label: "Right to left" },
    ];
    const RUNBOOK_TIDY_DIRECTIONS = TIDY_DIRECTIONS.filter((direction) =>
        ["TB", "LR", "RL"].includes(direction.key),
    );
    const TIDY_STYLES = [
        { key: "hierarchy", label: "Hierarchy tree" },
        { key: "linear", label: "Linear steps" },
    ];
    const TIDY_SPACING = [
        { key: "tight", label: "Tight", node: 34, rank: 70 },
        { key: "normal", label: "Normal", node: 60, rank: 110 },
        { key: "roomy", label: "Roomy", node: 100, rank: 180 },
    ];

    const remembered = (key, fallback, allowed) => {
        try {
            const v = localStorage.getItem(key);
            return allowed.includes(v) ? v : fallback;
        } catch (e) {
            return fallback;
        }
    };
    let tidyDir = $state(
        remembered(
            "zar-tidy-dir",
            untrack(() => runbookFlow) ? "LR" : "TB",
            TIDY_DIRECTIONS.map((d) => d.key),
        ),
    );
    let tidySpace = $state(
        remembered(
            "zar-tidy-space",
            "normal",
            TIDY_SPACING.map((s) => s.key),
        ),
    );
    let tidyStyle = $state(
        remembered(
            "zar-tidy-style",
            "hierarchy",
            TIDY_STYLES.map((style) => style.key),
        ),
    );
    let tidyMenu = $state(false);

    const tidyDirMeta = $derived(
        TIDY_DIRECTIONS.find((d) => d.key === tidyDir) ?? TIDY_DIRECTIONS[0],
    );

    function chooseTidy(dir, space) {
        tidyDir = dir ?? tidyDir;
        tidySpace = space ?? tidySpace;
        try {
            localStorage.setItem("zar-tidy-dir", tidyDir);
            localStorage.setItem("zar-tidy-space", tidySpace);
        } catch (e) {
            /* private mode: the choice just doesn't outlive the session */
        }
        tidyMenu = false;
        tidy();
    }

    function chooseTidyStyle(style) {
        tidyStyle = style;
        try {
            localStorage.setItem("zar-tidy-style", tidyStyle);
        } catch (e) {
            /* private mode: the choice just doesn't outlive the session */
        }
        tidyMenu = false;
        tidy();
    }

    function tidy({
        focusNodeId = null,
        announce = true,
        save = true,
        localBranch = false,
    } = {}) {
        closeMenu();
        if (nodes.length === 0) return;
        // A tidy owns every position it touches; undoing an old push afterwards would tear
        // holes in the drawing it just made.
        branchRoomShifts.clear();
        const beforeLayout = nodes;

        const space =
            TIDY_SPACING.find((sp) => sp.key === tidySpace) ?? TIDY_SPACING[1];
        if (runbookFlow && focusNodeId && localBranch) {
            const laid = layoutRunbookBranch(
                nodes,
                edges,
                focusNodeId,
                tidyDir,
                space,
                tidyStyle,
            );
            const preserved = preserveRunbookExpansionContext(
                beforeLayout,
                laid,
                edges,
                focusNodeId,
                tidyDir,
            );
            /*
             * Then the same group-level de-overlap the full tidy ends with. The preserve pass
             * moves only groups it can reach as DOWNSTREAM of the expanded one — on a simple
             * phase chain that is everyone who matters, which is why the synthetic flows
             * looked fine. An imported flow connects its groups by dependencies in every
             * direction, most of its groups are downstream of nothing, and the expanded
             * branch was landing straight on top of them: measured on a real one, six card
             * overlaps from a single expansion. Separation translates whole groups, so the
             * branch's internal chain survives untouched.
             */
            nodes = sizeParents(
                separateRunbookPhaseBranches(preserved, edges, tidyDir),
            );
            edges = separateEdgeLabels(nodes, edges, tidyDir);
            if (save) scheduleSave();
            focusRunbookNode(focusNodeId, 400);

            return;
        }
        if (runbookFlow && tidyStyle === "linear") {
            nodes = sizeParents(
                layoutRunbookLine(
                    nodes,
                    tidyDir === "BT" ? "TB" : tidyDir,
                    space.rank,
                ),
            );
            edges = separateEdgeLabels(nodes, edges, tidyDir);
            if (announce) {
                onStatus?.(
                    `Tidied as linear steps ${tidyDirMeta.label.toLowerCase()}`,
                );
            }
            if (save) scheduleSave();
            focusNodeId
                ? focusRunbookNode(focusNodeId, 400)
                : setTimeout(
                      () => fitView({ duration: 400, padding: 0.2 }),
                      60,
                  );

            return;
        }

        // Cards INSIDE a parent are laid out by their parent, not by the graph: dagre would
        // scatter them across the canvas and break the containment they were put in.
        const loose = nodes.filter((n) => !n.hidden && !n.parentId);

        const g = new dagre.graphlib.Graph();
        g.setDefaultEdgeLabel(() => ({}));
        // A rank runs ACROSS the flow, so which gap matters swaps with the axis. Cards are far
        // wider than they are tall, so a horizontal flow needs more room between ranks and less
        // within one — reusing the vertical numbers gives cramped columns and wasted rows.
        const horizontal = tidyDir === "LR" || tidyDir === "RL";
        const layoutSpacing = runbookFlow
            ? runbookHierarchySpacing(tidyDir, space)
            : {
                  nodesep: horizontal
                      ? Math.round(space.node * 0.6)
                      : space.node,
                  ranksep: horizontal
                      ? Math.round(space.rank * 1.5)
                      : space.rank,
              };
        g.setGraph({
            rankdir: tidyDir,
            nodesep: layoutSpacing.nodesep,
            ranksep: layoutSpacing.ranksep,
            marginx: 40,
            marginy: 40,
        });
        for (const n of loose) {
            g.setNode(n.id, {
                width: n.measured?.width ?? 230,
                height: n.measured?.height ?? 120,
            });
        }
        // What decides the ranks: the most structural kind of connection actually on the canvas,
        // and that kind alone. Everything else is drawn but gets no vote. See ./layout.js.
        const visibleIds = new Set(loose.map((node) => node.id));
        const onCanvas = (edge) =>
            !edge.hidden &&
            visibleIds.has(edge.source) &&
            visibleIds.has(edge.target);
        const plan = planLayout(edges.filter(onCanvas));

        for (const e of plan.ranked) {
            g.setEdge(e.source, e.target, { weight: e.weight });
        }

        // Events are the one card type with an inherent order, and it isn't the graph's. Chain
        // them oldest → newest so tidy produces a chronology rather than a hierarchy that
        // happens to contain dates. Undated events keep their place at the end. Skipped when the
        // canvas has a STRUCTURE — a family tree or a dependency map with dated events would
        // otherwise be pulled into a timeline and lose its shape.
        const dated = plan.structural
            ? []
            : loose
                  .filter(
                      (n) =>
                          n.data?.cardType === "research_events" &&
                          n.data?.occurredOn,
                  )
                  .sort((a, b) =>
                      String(a.data.occurredOn).localeCompare(
                          String(b.data.occurredOn),
                      ),
                  );
        for (let i = 1; i < dated.length; i++) {
            g.setEdge(dated[i - 1].id, dated[i].id, { weight: 10 });
        }

        dagre.layout(g);

        nodes = sizeParents(
            nodes.map((node) => {
                if (node.hidden || node.parentId) return node;
                const laid = g.node(node.id);

                return laid
                    ? {
                          ...node,
                          position: {
                              x: laid.x - laid.width / 2,
                              y: laid.y - laid.height / 2,
                          },
                      }
                    : node;
            }),
        );
        if (runbookFlow) {
            const separated = separateRunbookPhaseBranches(
                nodes,
                edges,
                tidyDir,
            );
            nodes = sizeParents(separated);
        }
        edges = separateEdgeLabels(nodes, edges, tidyDir);

        // Naming what ranked the canvas turns a surprising shape into an explicable one:
        // "tidied by containment" tells you which connections you'd change to get another.
        const why =
            dated.length > 1
                ? `${dated.length} events in date order`
                : plan.label
                  ? `by ${plan.label}`
                  : "";
        if (announce) {
            onStatus?.(
                `Tidied ${tidyDirMeta.label.toLowerCase()}${why ? " — " + why : ""}`,
            );
        }
        if (save) scheduleSave();
        focusNodeId
            ? focusRunbookNode(focusNodeId, 400)
            : setTimeout(() => fitView({ duration: 400, padding: 0.2 }), 60);
    }

    // ── Drag a card type out of the + Add menu ───────────────────────────────────
    const CARD_MIME = "application/x-zar-card";

    function startCardDrag(event, handle, label) {
        event.dataTransfer.effectAllowed = "copy";
        event.dataTransfer.setData(
            CARD_MIME,
            JSON.stringify({ handle, label }),
        );
        // Plain-text fallback keeps the drag legal in browsers that ignore custom types.
        event.dataTransfer.setData("text/plain", label);
        draggingType = handle;
    }

    /**
     * Place a card at a canvas position.
     *
     * A research card is a blank thought — make it and name it in place. A card of one of
     * YOUR content types is different: it stands for an actual record, so dropping one opens
     * the configure step (which entry, and which of its fields the research should use)
     * rather than guessing and leaving you to undo it.
     */
    async function dropCard(handle, label, at) {
        if (!CARD_TYPES.some((t) => t.handle === handle)) {
            window.Livewire?.dispatch?.("research-open-pinner", {
                type: handle,
                at,
            });

            return;
        }
        const placeholder = `New ${label.toLowerCase()}`;
        const node = await createCard(handle, placeholder, at);
        if (!node) return;
        // The card mounts straight into its name field with the placeholder selected, so a
        // new card is named by typing — never by hunting for where the title lives.
        renaming = {
            nodeId: node.id,
            entryId: node.data.entryId,
            value: placeholder,
        };
        nodes = nodes.map((n) =>
            n.id === node.id ? { ...n, data: { ...n.data, editing: true } } : n,
        );
    }

    /**
     * Pasted prose becomes a note card carrying the text.
     *
     * The title is the first line — how a person would name it anyway — and the whole paste is
     * the body, so nothing has to be retyped. Capture that costs a second step usually doesn't
     * happen at all.
     */
    async function dropNote(text, at) {
        const firstLine =
            text.split(/\r?\n/).find((line) => line.trim() !== "") ??
            "Pasted note";
        const title =
            firstLine.trim().slice(0, 70) +
            (firstLine.trim().length > 70 ? "…" : "");

        banner = { text: "Saved as a note.", cls: "rf-info" };
        setTimeout(() => (banner = null), 4000);
        await createCard("research_notes", title, at, text);
    }

    // ── Drag & drop images / files → uploaded as supporting-doc cards ─────────────
    function onDragOver(e) {
        if (!canEdit) return;
        const types = [...(e.dataTransfer?.types ?? [])];
        const isCard = types.includes(CARD_MIME) || draggingType !== null;
        // "Files" covers folders too — the browser doesn't distinguish until the drop.
        if (!isCard && !(types.includes("Files") && attachUrl)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        dropHover = true;
    }
    function onDragLeave(e) {
        if (e.currentTarget === e.target) dropHover = false;
    }
    async function onDrop(e) {
        dropHover = false;
        if (!canEdit) return;

        // A card type dragged out of the + Add menu lands exactly where it was dropped.
        const payload = e.dataTransfer?.getData(CARD_MIME);
        if (payload) {
            e.preventDefault();
            draggingType = null;
            try {
                const { handle, label } = JSON.parse(payload);
                await dropCard(
                    handle,
                    label,
                    screenToFlowPosition({ x: e.clientX, y: e.clientY }),
                );
            } catch (err) {
                onStatus?.("Could not add card");
            }

            return;
        }

        if (!attachUrl) return;

        // A FOLDER drop is different from a file drop: it has shape. Recursing it and reporting
        // what's inside — 34 images, 12 PDFs, across 5 sub-folders — is the difference between
        // "some files arrived" and knowing what you just brought in.
        const entries = [...(e.dataTransfer?.items ?? [])]
            .map((item) => item.webkitGetAsEntry?.())
            .filter((entry) => entry?.isDirectory);
        if (entries.length > 0) {
            e.preventDefault();
            await dropFolders(
                entries,
                screenToFlowPosition({ x: e.clientX, y: e.clientY }),
            );

            return;
        }

        const files = [...(e.dataTransfer?.files ?? [])];
        if (files.length === 0) return;
        e.preventDefault();
        await uploadFiles(
            files,
            screenToFlowPosition({ x: e.clientX, y: e.clientY }),
        );
    }

    /** Every file under a dropped directory, with the sub-folder count. */
    async function readDirectory(
        entry,
        into = { files: [], folders: 0 },
        depth = 0,
    ) {
        if (depth > 6) return into; // a symlink loop shouldn't hang the browser
        const reader = entry.createReader();

        // readEntries returns at most ~100 at a time and signals the end with an empty batch.
        for (;;) {
            const batch = await new Promise((resolve) =>
                reader.readEntries(resolve, () => resolve([])),
            );
            if (batch.length === 0) break;
            for (const child of batch) {
                if (child.isDirectory) {
                    into.folders++;
                    await readDirectory(child, into, depth + 1);
                } else {
                    await new Promise((resolve) =>
                        child.file((f) => {
                            into.files.push(f);
                            resolve();
                        }, resolve),
                    );
                }
            }
        }

        return into;
    }

    /** How many of what, so the folder card says what it holds. */
    function describeFiles(files, folders) {
        const kinds = {};
        for (const file of files) {
            const kind = (file.type.split("/")[0] || "file").replace(
                "application",
                "document",
            );
            kinds[kind] = (kinds[kind] ?? 0) + 1;
        }
        const parts = Object.entries(kinds)
            .sort((a, b) => b[1] - a[1])
            .map(([kind, n]) => `${n} ${kind}${n === 1 ? "" : "s"}`);
        const bytes = files.reduce((total, f) => total + f.size, 0);

        return { parts, bytes, folders };
    }

    /**
     * A dropped folder becomes a card holding its files.
     *
     * The card lands first with the full inventory — everything found, including what won't be
     * uploaded — so the count is honest even where the upload is capped. Files nest INSIDE it,
     * which is what the folder meant in the first place.
     */
    async function dropFolders(entries, at) {
        const CAP = 40;

        for (const [index, entry] of entries.entries()) {
            banner = { text: `Reading “${entry.name}”…`, cls: "rf-info" };
            const { files, folders } = await readDirectory(entry);
            const { parts, bytes } = describeFiles(files, folders);
            const megabytes = (bytes / 1024 / 1024).toFixed(1);

            const summary = [
                `${files.length} file${files.length === 1 ? "" : "s"}` +
                    (folders ? ` in ${folders + 1} folders` : "") +
                    ` · ${megabytes} MB`,
                parts.length ? parts.join(", ") : "nothing readable",
                files.length > CAP
                    ? `\nOnly the first ${CAP} were added as cards — the rest are listed here but not uploaded.`
                    : "",
            ]
                .filter(Boolean)
                .join("\n");

            const folderAt = { x: at.x + index * 360, y: at.y };
            const card = await createCard(
                "research_notes",
                entry.name,
                folderAt,
                summary,
                "folder",
            );
            if (!card) continue;

            // Inside the folder card, in a grid — the nesting IS the folder.
            for (const [i, file] of files.slice(0, CAP).entries()) {
                banner = {
                    text: `Adding ${file.name} (${i + 1}/${Math.min(files.length, CAP)})…`,
                    cls: "rf-info",
                };
                await uploadFiles(
                    [file],
                    { x: 16 + (i % 3) * 240, y: 60 + Math.floor(i / 3) * 150 },
                    card.id,
                );
            }

            nodes = sizeParents(nodes);
            notifyCardsChanged();
            banner = {
                text: `“${entry.name}” — ${summary.split("\n")[0]}.`,
                cls: "rf-info",
            };
            setTimeout(() => (banner = null), 9000);
        }
    }

    /** Upload dropped OR pasted files as cards. One path, so both feel the same. */
    async function uploadFiles(files, at, parentId = null) {
        for (const [i, file] of files.entries()) {
            banner = { text: `Uploading ${file.name}…`, cls: "rf-info" };
            const fd = new FormData();
            fd.append("file", file);
            fd.append("position[x]", String(at.x + i * 36));
            fd.append("position[y]", String(at.y + i * 36));
            if (parentId) fd.append("parentId", parentId);
            try {
                const res = await fetch(attachUrl, {
                    method: "POST",
                    headers: {
                        "X-CSRF-TOKEN": csrf,
                        Accept: "application/json",
                    },
                    body: fd,
                });
                const body = await res.json();
                if (body.ok) {
                    nodes = [...nodes, toFlowNode(body.node)];
                    if (body.version) version = body.version;
                    notifyCardsChanged();
                    // Documents are converted to text on the card — say so, since that's what
                    // makes them quotable/searchable rather than just an attachment.
                    banner = body.words
                        ? {
                              text: `Attached ${file.name} — ${body.words.toLocaleString()} words extracted`,
                              cls: "rf-info",
                          }
                        : { text: `Attached ${file.name}`, cls: "rf-info" };
                } else {
                    banner = {
                        text: body.error || `Could not attach ${file.name}`,
                        cls: "rf-warn",
                    };
                }
            } catch (err) {
                banner = {
                    text: `Upload failed for ${file.name}`,
                    cls: "rf-warn",
                };
            }
        }
        setTimeout(() => (banner = null), 5000);
    }

    const nodeTypes = { card: ResearchCard };
    const edgeTypes = { research: ResearchEdge };
    const defaultEdgeOptions = {
        type: "research",
        markerEnd: { type: MarkerType.ArrowClosed },
    };
</script>

<svelte:window
    onpaste={onPaste}
    onpointerdown={closeControlPanelMenusOnOutsidePointer}
/>
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
    bind:this={flowElement}
    class="research-flow"
    class:rf-drop={dropHover}
    style="width:100%;height:100%;position:relative"
    ondragover={onDragOver}
    ondragleave={onDragLeave}
    ondrop={onDrop}
>
    {#if (canEdit && showCardTools) || runbookFlow}
        <!-- One compact bar: the card types live in a dropdown so the toolbar can never
             outgrow the canvas and spill over the side panels. -->
        <div
            bind:this={controlPanelElement}
            class="rf-toolbar"
            class:rf-toolbar-minimized={controlPanel.minimized}
            style="left:{controlPanel.x}px;top:{controlPanel.y}px"
            aria-label={runbookFlow
                ? "Runbook flow controls"
                : "Research controls"}
        >
            <button
                type="button"
                class="rf-panel-drag"
                aria-label="Move controls"
                title="Drag controls"
                onpointerdown={startControlPanelDrag}
                onpointermove={moveControlPanel}
                onpointerup={stopControlPanelDrag}
                onpointercancel={stopControlPanelDrag}>⠿</button
            >
            {#if controlPanel.minimized}
                <span class="rf-panel-title">Controls</span>
            {:else}
                {#if canEdit && showCardTools}
                    {#if others.length}
                        <!-- Who else is on this workspace. Initials rather than photos: a stack of
                     avatars is decoration, and the only question anyone asks of it is "who". -->
                        <div
                            class="rf-people"
                            title="{others
                                .map((p) => p.name)
                                .join(', ')} also here"
                        >
                            {#each others.slice(0, 5) as person (person.id)}
                                <span
                                    class="rf-person"
                                    style="background:{person.color}"
                                    >{initialsOf(person.name)}</span
                                >
                            {/each}
                            {#if others.length > 5}<span
                                    class="rf-person rf-more"
                                    >+{others.length - 5}</span
                                >{/if}
                        </div>
                    {/if}
                    <div class="rf-type-wrap">
                        <button
                            class="rf-add"
                            class:rf-active={typeMenuOpen}
                            onclick={toggleTypeMenu}
                            title="Add a card — click to place it, or drag one onto the canvas"
                            >+ Add ▾</button
                        >
                        {#if typeMenuOpen}
                            <div class="rf-type-menu" role="menu">
                                <div class="rf-menu-head">
                                    Drag onto the canvas, or click to place
                                </div>
                                {#each CARD_TYPES as ct (ct.handle)}
                                    <button
                                        role="menuitem"
                                        draggable="true"
                                        ondragstart={(e) =>
                                            startCardDrag(
                                                e,
                                                ct.handle,
                                                ct.label,
                                            )}
                                        onclick={() =>
                                            addCard(ct.handle, ct.label)}
                                        title={ct.hint}
                                    >
                                        <span class="rf-menu-icon"
                                            >{ct.icon}</span
                                        >{ct.label}
                                    </button>
                                {/each}
                                <div class="rf-menu-head">
                                    From your content
                                </div>
                                <button
                                    role="menuitem"
                                    onclick={() => {
                                        typeMenuOpen = false;
                                        window.Livewire?.dispatch?.(
                                            "research-open-pinner",
                                        );
                                    }}
                                    title="Bring existing entries onto the workspace as data sources"
                                >
                                    <span class="rf-menu-icon">📌</span>Pin
                                    existing content…
                                </button>
                                {#if contentTypes.length}
                                    <div class="rf-menu-head">
                                        New, of another type
                                    </div>
                                    {#each contentTypes as ct (ct.handle)}
                                        <button
                                            role="menuitem"
                                            draggable="true"
                                            ondragstart={(e) =>
                                                startCardDrag(
                                                    e,
                                                    ct.handle,
                                                    ct.label,
                                                )}
                                            onclick={() =>
                                                addCard(ct.handle, ct.label)}
                                        >
                                            <span class="rf-menu-icon">🗂️</span
                                            >{ct.label}
                                        </button>
                                    {/each}
                                {/if}
                            </div>
                        {/if}
                    </div>
                    <span class="rf-sep"></span>
                    <button
                        class:rf-active={selectMode}
                        onclick={() => (selectMode = !selectMode)}
                        title="Toggle box-select (drag to select many)"
                        >▣ Select</button
                    >
                    <button
                        class:rf-active={proximity}
                        onclick={toggleProximity}
                        title="Proximity linking: drag a card next to another and they link on drop"
                        >⇢ Snap</button
                    >
                {/if}
                <span class="rf-split">
                    <button
                        class="rf-split-main"
                        onclick={() => tidy()}
                        title="Auto-arrange — {tidyDirMeta.label.toLowerCase()}"
                        >✦ Tidy {tidyDirMeta.glyph}</button
                    >
                    <button
                        class="rf-split-caret"
                        onclick={toggleTidyMenu}
                        aria-label="Tidy direction and spacing"
                        aria-expanded={tidyMenu}>▾</button
                    >
                    {#if tidyMenu}
                        <div class="rf-split-menu" role="menu">
                            {#if runbookFlow}
                                <div class="rf-menu-head">Style</div>
                                {#each TIDY_STYLES as style (style.key)}
                                    <button
                                        role="menuitem"
                                        class:rf-chosen={style.key ===
                                            tidyStyle}
                                        onclick={() =>
                                            chooseTidyStyle(style.key)}
                                    >
                                        <span class="rf-menu-icon"
                                            >{style.key === tidyStyle
                                                ? "✓"
                                                : ""}</span
                                        >{style.label}
                                    </button>
                                {/each}
                            {/if}
                            <div class="rf-menu-head">Direction</div>
                            {#each runbookFlow ? RUNBOOK_TIDY_DIRECTIONS : TIDY_DIRECTIONS as d (d.key)}
                                <button
                                    role="menuitem"
                                    class:rf-chosen={d.key === tidyDir}
                                    onclick={() => chooseTidy(d.key, null)}
                                >
                                    <span class="rf-menu-icon">{d.glyph}</span
                                    >{d.label}
                                </button>
                            {/each}
                            <div class="rf-menu-head">Spacing</div>
                            {#each TIDY_SPACING as sp (sp.key)}
                                <button
                                    role="menuitem"
                                    class:rf-chosen={sp.key === tidySpace}
                                    onclick={() => chooseTidy(null, sp.key)}
                                >
                                    <span class="rf-menu-icon"
                                        >{sp.key === tidySpace ? "✓" : ""}</span
                                    >{sp.label}
                                </button>
                            {/each}
                        </div>
                    {/if}
                </span>
                {#if runbookFlow}
                    <span class="rf-sep"></span>
                    <div class="rf-flow-count">
                        <strong>{runbookStats.visible}</strong> of {runbookStats.total}
                        nodes
                    </div>
                    <button
                        type="button"
                        onclick={collapseRunbookToPhases}
                        title="Show only phases and rolled-up connections"
                        >Phases</button
                    >
                    <button
                        type="button"
                        onclick={expandRunbook}
                        disabled={runbookExpanding ||
                            runbookStats.visible === runbookStats.total}
                        title="Load every phase, step, and sub-step in small batches"
                        >{runbookExpanding ? "Loading…" : "Expand all"}</button
                    >
                    <button
                        type="button"
                        onclick={collapseRunbookToPhases}
                        title="Collapse all steps back to their phases"
                        >Collapse all</button
                    >
                    <span class="rf-flow-divider"></span>
                    {#each [["hierarchy", "Structure"], ["sequence", "Sequence"], ["dependency", "Dependencies"], ["success", "Success"], ["failure", "Failure"]] as role (role[0])}
                        <button
                            type="button"
                            class="rf-role rf-role-{role[0]}"
                            class:rf-off={!enabledRunbookRoles.includes(
                                role[0],
                            )}
                            aria-pressed={enabledRunbookRoles.includes(role[0])}
                            onclick={() => toggleRunbookRole(role[0])}
                            >{role[1]}</button
                        >
                    {/each}
                {/if}
            {/if}
            <button
                type="button"
                class="rf-panel-toggle"
                aria-label={controlPanel.minimized
                    ? "Restore controls"
                    : "Minimize controls"}
                title={controlPanel.minimized
                    ? "Restore controls"
                    : "Minimize controls"}
                onclick={toggleControlPanel}
                >{controlPanel.minimized ? "+" : "−"}</button
            >
            {#if runbookExpanding && !branchExpansionNodeId}
                <div
                    class="rf-expand-skeleton"
                    role="status"
                    aria-live="polite"
                >
                    <div>
                        Loading in task order — {runbookStats.visible} of {runbookStats.total}
                        nodes
                    </div>
                    <span></span><span></span><span></span>
                </div>
            {/if}
        </div>
    {/if}
    {#if banner}
        <div class="rf-banner {banner.cls}">{banner.text}</div>
    {/if}
    {#if dropHover}
        <div class="rf-dropzone">
            Drop images or files to attach them as sources
        </div>
    {/if}
    {#if menu}
        <div
            class="rf-menu-backdrop"
            role="presentation"
            oncontextmenu={(e) => {
                e.preventDefault();
                closeMenu();
            }}
            onclick={closeMenu}
        ></div>
        <div
            class="rf-menu"
            style="left:{menu.x}px; top:{menu.y}px"
            role="menu"
        >
            {#if menu.kind === "node"}
                <button role="menuitem" onclick={() => openReader(menu.entryId)}
                    >Open &amp; edit</button
                >
                {#if menu.cardType === "research_sources"}
                    <!-- Only meaningful for a card holding a FILE; the board decides whether
                         it can actually be broken apart and says so if not. -->
                    <button
                        role="menuitem"
                        onclick={() => {
                            window.Livewire?.dispatch?.(
                                "research-explode-file",
                                { entryId: menu.entryId },
                            );
                            closeMenu();
                        }}>Break into cards</button
                    >
                    <button
                        role="menuitem"
                        onclick={() =>
                            setCardStatus(
                                menu.entryId,
                                "review_status",
                                "approved",
                            )}>Approve source</button
                    >
                    <button
                        role="menuitem"
                        onclick={() =>
                            setCardStatus(
                                menu.entryId,
                                "review_status",
                                "reviewed",
                            )}>Mark reviewed</button
                    >
                {/if}
                {#if menu.cardType === "research_topics"}
                    <button
                        role="menuitem"
                        onclick={() =>
                            setCardStatus(menu.entryId, "readiness", "ready")}
                        >Mark ready</button
                    >
                {/if}
                {#if menu.cardType === "research_tasks"}
                    <button
                        role="menuitem"
                        onclick={() =>
                            setCardStatus(menu.entryId, "status", "doing")}
                        >Mark in progress</button
                    >
                    <button
                        role="menuitem"
                        onclick={() =>
                            setCardStatus(menu.entryId, "status", "done")}
                        >Mark done</button
                    >
                {/if}
                {#if menu.nested}
                    <button
                        role="menuitem"
                        onclick={() => {
                            unnest(menu.entryId);
                            closeMenu();
                        }}>Move out of its group</button
                    >
                {/if}
                {#if menu.holds}
                    <button
                        role="menuitem"
                        onclick={() => {
                            ungroup(menu.entryId);
                            closeMenu();
                        }}>Ungroup — release what’s inside</button
                    >
                {/if}
                <button
                    role="menuitem"
                    class="rf-danger"
                    onclick={() => deleteNodes([menu.nodeId])}
                    >Remove from workspace</button
                >
            {:else if menu.kind === "selection"}
                <div class="rf-menu-label">
                    {selectedNodeIds.length} selected
                </div>
                <button role="menuitem" onclick={connectSelected}
                    >Connect in a chain</button
                >
                <!-- Fan-in / fan-out: the everyday shape of research (many sources → one
                     claim, one topic → many questions) without drawing each line by hand. -->
                <button
                    role="menuitem"
                    onclick={() => connectSelectedTo(menu.nodeId, true)}
                    >Connect all → this card</button
                >
                <button
                    role="menuitem"
                    onclick={() => connectSelectedTo(menu.nodeId, false)}
                    >Connect this card → all</button
                >
                <button
                    role="menuitem"
                    class="rf-danger"
                    onclick={() => deleteNodes(selectedNodeIds)}
                    >Remove selected</button
                >
            {:else if menu.kind === "edge"}
                <div class="rf-menu-label">Evidence</div>
                {#each KINDS as kind (kind)}
                    <button
                        role="menuitem"
                        class:rf-on={menu.edgeKind === kind}
                        onclick={() => setEdgeKind(menu.edgeId, kind)}
                        >{kind.replaceAll("_", " ")}</button
                    >
                {/each}
                <div class="rf-menu-label">Structure &amp; systems</div>
                {#each STRUCTURAL as kind (kind)}
                    <button
                        role="menuitem"
                        class:rf-on={menu.edgeKind === kind}
                        onclick={() => setEdgeKind(menu.edgeId, kind)}
                        >{kind.replaceAll("_", " ")}</button
                    >
                {/each}
                <button
                    role="menuitem"
                    class="rf-danger"
                    onclick={() => deleteEdge(menu.edgeId)}
                    >Delete connection</button
                >
            {:else if menu.kind === "pane"}
                <button
                    role="menuitem"
                    onclick={() => addCard("research_topics", null, menu.flow)}
                    >Add topic here</button
                >
                <button
                    role="menuitem"
                    onclick={() => addCard("research_claims", null, menu.flow)}
                    >Add claim here</button
                >
                <button
                    role="menuitem"
                    onclick={() => addCard("research_notes", null, menu.flow)}
                    >Add note here</button
                >
                <button
                    role="menuitem"
                    onclick={() =>
                        addCard("research_questions", null, menu.flow)}
                    >Add question here</button
                >
                <button
                    role="menuitem"
                    onclick={() => addCard("research_sources", null, menu.flow)}
                    >Add source here</button
                >
                <button
                    role="menuitem"
                    onclick={() =>
                        addCard("research_hypotheses", null, menu.flow)}
                    >Add hypothesis here</button
                >
                <button
                    role="menuitem"
                    onclick={() =>
                        addCard("research_definitions", null, menu.flow)}
                    >Add definition here</button
                >
                <button
                    role="menuitem"
                    onclick={() => addCard("research_tasks", null, menu.flow)}
                    >Add task here</button
                >
                <button role="menuitem" onclick={() => tidy()}
                    >Tidy the workspace {tidyDirMeta.glyph}</button
                >
            {/if}
        </div>
    {/if}
    <SvelteFlow
        bind:nodes
        bind:edges
        {nodeTypes}
        {edgeTypes}
        {defaultEdgeOptions}
        connectionMode={ConnectionMode.Loose}
        onconnect={handleConnect}
        onedgeclick={onEdgeClick}
        onselectionchange={onSelectionChange}
        onnodecontextmenu={openNodeMenu}
        onedgecontextmenu={openEdgeMenu}
        onpanecontextmenu={openPaneMenu}
        onnodedrag={onNodeDrag}
        onnodedragstop={onNodeDragStop}
        ondelete={scheduleSave}
        selectionOnDrag={selectMode}
        panOnDrag={selectMode ? [1] : true}
        selectionKeyCode={"Shift"}
        multiSelectionKeyCode={"Shift"}
        deleteKey={canEdit ? ["Backspace", "Delete"] : null}
        nodesDraggable={canEdit}
        nodesConnectable={canEdit}
        fitView={!runbookFlow}
        proOptions={{ hideAttribution: true }}
    >
        <Background />
        {#if runbookFlow}
            <ViewportPortal>
                {#each runbookPhaseFrames as frame (frame.id)}
                    <div
                        class="rf-phase-frame"
                        class:rf-phase-collapsed={frame.collapsed}
                        data-phase-frame={frame.id}
                        data-phase-depth={frame.depth}
                        style="--phase-color:{frame.color};transform:translate({frame.x}px,{frame.y}px);width:{frame.width}px;height:{frame.height}px"
                    >
                        <div class="rf-phase-frame-label">
                            <span>{frame.label}</span>
                            <small>{frame.visibleChildren} visible</small>
                        </div>
                    </div>
                {/each}
            </ViewportPortal>
        {/if}
        {#if showControls}<Controls />{/if}
        {#if showMinimap}<MiniMap />{/if}
    </SvelteFlow>
</div>

<style>
    .research-flow.rf-drop {
        outline: 2px dashed #6366f1;
        outline-offset: -8px;
    }
    .rf-phase-frame {
        position: absolute;
        left: 0;
        top: 0;
        z-index: -1;
        border: 2px dashed var(--phase-color, #64748b);
        border-radius: 8px;
        background: transparent;
        pointer-events: none;
        transition:
            transform 180ms ease-out,
            opacity 180ms ease-out;
    }
    .rf-phase-frame-label {
        position: absolute;
        left: 12px;
        top: -12px;
        display: flex;
        max-width: calc(100% - 24px);
        align-items: center;
        gap: 7px;
        padding: 2px 7px;
        border: 1px solid
            color-mix(in srgb, var(--phase-color) 45%, transparent);
        border-radius: 5px;
        background: color-mix(in srgb, canvas 94%, transparent);
        color: var(--phase-color);
        font-size: 10px;
        font-weight: 750;
        line-height: 16px;
        white-space: nowrap;
        backdrop-filter: blur(6px);
    }
    .rf-phase-frame-label span {
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .rf-phase-frame-label small {
        flex: none;
        opacity: 0.68;
        font-size: 9px;
        font-weight: 600;
    }
    .rf-phase-collapsed {
        border-style: dashed;
        opacity: 0.72;
    }
    :global(.dark) .rf-phase-frame-label {
        background: rgba(24, 24, 27, 0.94);
    }
    @media (prefers-reduced-motion: reduce) {
        .rf-phase-frame {
            transition: none;
        }
    }
    .rf-flow-count {
        padding: 0 5px;
        color: var(--rc-faint);
        font-size: 11px;
        font-variant-numeric: tabular-nums;
    }
    .rf-flow-count strong {
        color: var(--rc-ink);
    }
    .rf-flow-divider {
        width: 1px;
        height: 22px;
        margin: 0 2px;
        background: rgba(128, 128, 128, 0.3);
    }
    .rf-toolbar .rf-role {
        position: relative;
        padding-left: 19px;
    }
    .rf-toolbar .rf-role::before {
        content: "";
        position: absolute;
        left: 7px;
        top: 50%;
        width: 7px;
        height: 7px;
        border-radius: 999px;
        background: currentColor;
        transform: translateY(-50%);
    }
    .rf-role-hierarchy {
        color: #64748b !important;
    }
    .rf-role-sequence {
        color: #0284c7 !important;
    }
    .rf-role-dependency {
        color: #b45309 !important;
    }
    .rf-role-success {
        color: #15803d !important;
    }
    .rf-role-failure {
        color: #dc2626 !important;
    }
    :global(.dark) .rf-role-hierarchy {
        color: #94a3b8 !important;
    }
    :global(.dark) .rf-role-sequence {
        color: #38bdf8 !important;
    }
    :global(.dark) .rf-role-dependency {
        color: #fbbf24 !important;
    }
    :global(.dark) .rf-role-success {
        color: #4ade80 !important;
    }
    :global(.dark) .rf-role-failure {
        color: #f87171 !important;
    }
    .rf-toolbar .rf-role.rf-off {
        opacity: 0.42;
        filter: grayscale(1);
    }
    .rf-toolbar button:disabled {
        cursor: wait;
        opacity: 0.6;
    }
    .rf-expand-skeleton {
        position: absolute;
        top: calc(100% + 8px);
        right: 0;
        z-index: 31;
        width: 220px;
        padding: 8px;
        border: 1px solid rgba(128, 128, 128, 0.28);
        border-radius: 8px;
        background: color-mix(in srgb, canvas 92%, transparent);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        backdrop-filter: blur(8px);
        color: var(--rc-faint);
        font-size: 10px;
    }
    .rf-expand-skeleton span {
        display: block;
        height: 8px;
        margin-top: 7px;
        border-radius: 3px;
        background: rgba(99, 102, 241, 0.16);
        animation: rf-skeleton 900ms ease-in-out infinite alternate;
    }
    .rf-expand-skeleton span:nth-of-type(2) {
        width: 78%;
        animation-delay: 100ms;
    }
    .rf-expand-skeleton span:nth-of-type(3) {
        width: 56%;
        animation-delay: 200ms;
    }
    @keyframes rf-skeleton {
        to {
            opacity: 0.35;
        }
    }
    @media (prefers-reduced-motion: reduce) {
        .rf-expand-skeleton span {
            animation: none;
        }
    }
    /* Anchored to the canvas's own left edge and width-capped: a centred bar sized by its
       content used to grow wider than the canvas once the side panels opened, and spill
       over both of them. It can't now — it clips to the canvas and wraps instead. */
    .rf-toolbar {
        position: absolute;
        z-index: 6;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px;
        width: fit-content;
        max-width: calc(100% - 20px);
        padding: 6px;
        border-radius: 10px;
        background: color-mix(in srgb, canvas 85%, transparent);
        backdrop-filter: blur(6px);
        border: 1px solid rgba(128, 128, 128, 0.25);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
    }
    .rf-toolbar-minimized {
        flex-wrap: nowrap;
        width: auto;
        padding: 4px;
    }
    .rf-panel-drag,
    .rf-panel-toggle {
        display: grid;
        flex: none;
        place-items: center;
        width: 28px;
        min-height: 28px;
        padding: 0 !important;
    }
    .rf-panel-drag {
        cursor: grab !important;
        touch-action: none;
        color: var(--rc-faint) !important;
    }
    .rf-panel-drag:active {
        cursor: grabbing !important;
    }
    .rf-panel-toggle {
        font-size: 16px !important;
        line-height: 1;
    }
    .rf-panel-title {
        padding: 0 2px;
        color: var(--rc-faint);
        font-size: 11px;
        font-weight: 700;
    }
    .rf-people {
        display: flex;
        align-items: center;
        gap: -4px;
        padding-right: 4px;
    }
    .rf-person {
        display: grid;
        place-items: center;
        width: 22px;
        height: 22px;
        margin-right: -6px;
        border-radius: 999px;
        border: 2px solid var(--rc-surface, #fff);
        font-size: 9px;
        font-weight: 700;
        color: #fff;
        letter-spacing: 0.02em;
    }
    .rf-person.rf-more {
        background: #71717a;
    }
    .rf-toolbar button {
        font-size: 12px;
        font-weight: 600;
        padding: 4px 10px;
        border-radius: 7px;
        border: 1px solid rgba(128, 128, 128, 0.3);
        background: transparent;
        color: inherit;
        cursor: pointer;
        white-space: nowrap;
    }
    .rf-toolbar button:hover {
        background: rgba(99, 102, 241, 0.12);
        border-color: #6366f1;
    }
    .rf-toolbar button.rf-active {
        background: #6366f1;
        color: #fff;
        border-color: #6366f1;
    }
    .rf-sep {
        width: 1px;
        align-self: stretch;
        background: rgba(128, 128, 128, 0.3);
        margin: 2px 2px;
    }
    .rf-type-wrap {
        position: relative;
    }
    .rf-type-menu {
        position: absolute;
        top: calc(100% + 6px);
        left: 0;
        z-index: 30;
        min-width: 180px;
        max-height: 320px;
        overflow-y: auto;
        padding: 4px;
        border-radius: 10px;
        background: var(--rc-surface);
        color: var(--rc-ink);
        border: 1px solid rgba(128, 128, 128, 0.3);
        box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);
        display: flex;
        flex-direction: column;
        gap: 2px;
    }
    .rf-type-menu button {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        text-align: left;
        border: 0;
        background: transparent;
        font-size: 12px;
        font-weight: 500;
        padding: 6px 8px;
        border-radius: 7px;
        cursor: grab;
        color: inherit;
    }
    .rf-type-menu button:hover {
        background: rgba(99, 102, 241, 0.12);
    }
    .rf-type-menu button:active {
        cursor: grabbing;
    }
    .rf-menu-icon {
        font-size: 13px;
        line-height: 1;
    }
    .rf-menu-head {
        font-size: 9px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        opacity: 0.45;
        padding: 6px 8px 2px;
    }
    .rf-add {
        min-width: 74px;
    }
    .rf-banner {
        position: absolute;
        top: 56px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 10;
        max-width: 90%;
        padding: 6px 12px;
        border-radius: 8px;
        font-size: 12px;
        font-weight: 500;
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
    }
    .rf-info {
        background: #eef2ff;
        color: #3730a3;
    }
    .rf-warn {
        background: #fef3c7;
        color: #92400e;
    }
    :global(.dark) .rf-info {
        background: #312e81;
        color: #c7d2fe;
    }
    :global(.dark) .rf-warn {
        background: #4a3210;
        color: #fde68a;
    }
    .rf-dropzone {
        position: absolute;
        inset: 24px;
        z-index: 9;
        display: grid;
        place-items: center;
        pointer-events: none;
        border-radius: 16px;
        border: 2px dashed #6366f1;
        background: rgba(99, 102, 241, 0.06);
        font-size: 13px;
        font-weight: 600;
        color: #4f46e5;
    }
    :global(.dark) .rf-dropzone {
        color: #c7d2fe;
        background: rgba(99, 102, 241, 0.14);
    }

    /* Right-click context menu */
    .rf-menu-backdrop {
        position: fixed;
        inset: 0;
        z-index: 29;
    }
    /* Split control: the main half re-runs the remembered choice, the caret changes it. */
    .rf-split {
        position: relative;
        display: inline-flex;
    }
    .rf-split-main {
        border-top-right-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
    }
    .rf-split-caret {
        border-top-left-radius: 0 !important;
        border-bottom-left-radius: 0 !important;
        margin-left: -1px;
        padding-left: 6px !important;
        padding-right: 6px !important;
    }
    .rf-split-menu {
        position: absolute;
        z-index: 30;
        top: calc(100% + 6px);
        right: 0;
        min-width: 176px;
        padding: 4px;
        border-radius: 10px;
        background: var(--rc-surface);
        color: var(--rc-ink);
        border: 1px solid var(--rc-line);
        box-shadow: 0 12px 30px var(--rc-shadow);
        display: flex;
        flex-direction: column;
        gap: 2px;
    }
    .rf-split-menu button {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        border: 0 !important;
        border-radius: 6px !important;
        padding: 5px 8px !important;
        font-weight: 500 !important;
        text-align: left;
    }
    .rf-split-menu button:hover {
        background: var(--rc-hover, rgba(128, 128, 128, 0.12));
    }
    .rf-split-menu .rf-chosen {
        background: color-mix(
            in srgb,
            var(--rc-accent, #0ea5e9) 14%,
            transparent
        );
    }

    .rf-menu {
        position: fixed;
        z-index: 30;
        min-width: 190px;
        padding: 4px;
        border-radius: 10px;
        background: var(--rc-surface);
        color: var(--rc-ink);
        border: 1px solid var(--rc-line);
        box-shadow: 0 12px 30px var(--rc-shadow);
        display: flex;
        flex-direction: column;
        gap: 2px;
    }
    .rf-menu button {
        width: 100%;
        text-align: left;
        border: 0;
        background: transparent;
        font-size: 12px;
        font-weight: 500;
        padding: 7px 9px;
        border-radius: 7px;
        cursor: pointer;
        color: inherit;
    }
    .rf-menu button:hover {
        background: rgba(99, 102, 241, 0.12);
    }
    .rf-menu button.rf-danger {
        color: #dc2626;
    }
    :global(.dark) .rf-menu button.rf-danger {
        color: #f87171;
    }
    .rf-menu button.rf-danger:hover {
        background: rgba(220, 38, 38, 0.1);
    }
    /* The edge menu lists every kind, so it can outgrow a short screen. */
    .rf-menu {
        max-height: min(70vh, 460px);
        overflow-y: auto;
    }
    .rf-menu button.rf-on {
        font-weight: 700;
        color: var(--rc-ink, inherit);
    }
    .rf-menu button.rf-on::after {
        content: " ✓";
        opacity: 0.6;
    }
    .rf-menu-label {
        font-size: 10px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        opacity: 0.5;
        padding: 4px 9px 2px;
    }
</style>
